import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const translations = {
  en: {
    translation: {
      shared: {
        hoursShort: 'h',
        minutesShort: 'm',
        hoursLong: 'hours',
        minutesLong: 'minutes',
        secondsShort: 's',
        secondsLong: 'seconds'
      },
      legacyViews: {
        redirectToNewAssignments: {
          title: 'New View Available',
          message:
            "We've migrated to a new and improved assignment view. You'll be automatically redirected in {{countdown}}",
          goToNewViewButton: 'Go to New View Now'
        }
      },
      domains: {
        legal: {
          backToHome: 'Back to site',
          titles: {
            terms: 'Terms of Service',
            privacy: 'Privacy Policy',
            dpo: 'Data Protection Officer',
            returns: 'Return & Withdrawal Policy',
            contact: 'Contact'
          },
          footer: {
            securePayments: 'Secure payments:',
            terms: 'Terms of Service',
            privacy: 'Privacy Policy',
            dpo: 'Data Protection Officer',
            returns: 'Return & Withdrawal Policy',
            contact: 'Contact'
          }
        },
        auth: {
          approvals: {
            title: 'Confirm our terms and conditions',
            message:
              'To ensure you have full access to all features and also take care of the clarity and security of your data, please review and accept the following documents and give your consent.',
            selectAll: 'Select all',
            privacyPolicy: 'Privacy Policy',
            termsOfService: 'Terms of Service',
            submit: 'Submit',
            iHaveReadAndAccepted: 'I have read and accepted'
          },
          sessionLost: {
            title: 'Your session expired',
            message:
              'You were signed in here before, but your session is no longer valid. Sign in again to continue.',
            signInAgain: 'Sign in again'
          }
        },
        imprvCoins: {
          coinChip: {
            info: 'Every AI response has a cost, shown in Imprv Coins – this is how many this response used'
          }
        },
        mediaitems: {
          accessError: {
            blockingNetwork: {
              header: 'Most likely your network is blocking the content',
              text: 'Please check your network settings or contact your administrator'
            },
            aborted: {
              header: 'Media playback was aborted',
              text: 'Please try playing the media again'
            },
            network: {
              header: 'Network issue detected',
              text: 'Please check your network settings or contact your administrator'
            },
            decode: {
              header: 'Unable to decode the content',
              text: 'The content might be corrupted or in an unsupported format'
            }
          }
        },
        marketing: {
          createGoalCategorySubscription: 'Please notify me',
          creatingGoalCategorySubscription: 'Processing ...',
          emailPlaceholder: 'Enter your email',
          invalidEmailError: 'Please enter a valid email address',
          consentRequired: 'You must accept the marketing consent in order to subscribe',
          alreadySubscribed:
            'You are subscribed, we will notify you when first courses in this category are available'
        },
        goals: {
          categories: {
            family: {
              label: 'Family',
              introText: 'Build an environment where potential can grow.',
              ctaText:
                'Family is a living organism that requires attention, strategy, and a deep commitment to authenticity. At imprv.ai, we see parenthood as a continuous path of growth and mastery. We begin with mentoring support at the very start of life, but our vision goes further — creating space for conscious parents who want to combine professional ambition with building lasting, meaningful bonds. Join the list to gain access to knowledge that bridges human development science with the practice of everyday closeness.'
            },
            ai: {
              label: 'AI',
              introText: 'Reclaim time. Amplify your potential.',
              ctaText:
                'We don’t teach AI so you can do less. We teach AI so you can reach for more. At imprv.ai, we treat algorithms as an extension of your capabilities — tools that handle routine so you can focus on creation, strategy, and what makes you uniquely human. Sign up to be the first to learn when we open access to methods that turn your ambition into a new reality.'
            },
            programming: {
              label: 'Programming',
              introText: 'Build the world on your own terms.',
              ctaText:
                'Code is more than lines of text — it is the foundation of your agency. In a world where technology sets the rules, we teach you how to take the controls. Programming at imprv.ai is about learning precise thinking and turning chaos into working solutions. Be the first to know when we launch courses that will transform you from a consumer of technology into its architect.'
            },
            dataAnalysis: {
              label: 'Data Analysis',
              introText: 'Learn to read between the lines.',
              ctaText:
                'We live in a constant stream of information noise that erodes our sense of agency. At imprv.ai, we teach how to turn data chaos into clear insights and effective decisions. This is not a spreadsheet course — it is training in objective thinking about the world and your projects. Be the first to know when we open access to tools that help you see patterns where others see only randomness.'
            },
            science: {
              label: 'Science',
              introText: 'Understand the rules of the game.',
              ctaText:
                'Mathematics, physics, and logic are not just textbook formulas — they are the operating system of the world. We believe that understanding the fundamentals gives you an advantage no intuition can replace. We teach the sciences so you stop guessing and start predicting and designing with mathematical precision. Join the list to learn when we open courses that turn dry theory into your most powerful weapon.'
            },
            foreignLanguages: {
              label: 'Foreign Languages',
              introText: 'Install a new perspective.',
              ctaText:
                'Language is more than grammar — it is an operating system that allows you to act in a global environment. At imprv.ai, we teach languages as tools for acquiring knowledge and building relationships without barriers. We use AI to radically shorten the learning curve and focus on what matters most: the freedom to express your potential. Be the first to know when we open access to methods that let you think and create in a new language.'
            },
            business: {
              label: 'Business',
              introText: 'Build organisms, not just organizations.',
              ctaText:
                'At imprv.ai, we believe business is more than numbers in a spreadsheet — it is a living ecosystem driven by your vision and discipline. We teach a holistic approach where strategy meets psychology, and modern AI tools support human potential. Be the first to learn about courses that will teach you how to design ventures resilient to chaos, aligned with your values, and ready for new challenges.'
            },
            longevity: {
              label: 'Longevity',
              introText: 'Design your future.',
              ctaText:
                'We believe true growth is impossible without caring for the foundation — your body. We cannot allow time to take away our agency. Our holistic approach combines data, biotechnology, and daily discipline to give you an advantage you cannot buy with money: time in full strength. This is the science of being "more" not only today, but thirty years from now. Be the first to access knowledge that restores belief in the limitless potential of the human body.'
            },
            sportAndFitness: {
              label: 'Sport & Fitness',
              introText: 'Build the foundation for your potential.',
              ctaText:
                'We believe high ambition requires a powerful engine. Our approach to fitness is holistic: we combine movement science, recovery, and technology so your body can keep up with your mind. These are not ordinary workouts — this is optimization of your mobility and energy so your body never limits your plans. Join the waitlist and start training with intention.'
            },
            genius: {
              label: 'Genius',
              introText: 'Become more.',
              ctaText:
                'The modern world suppresses your potential with constant distractions. We restore your agency. The Genius category is the essence of imprv.ai — training for those who want to see more, understand faster, and build things that seem impossible. These are not ordinary courses; they are a process of becoming the best version of yourself. Be the first when we reveal what’s coming.'
            },
            art: {
              label: 'Art',
              introText: 'What no algorithm can replicate.',
              ctaText:
                'In a world dominated by technology, your creativity is the most valuable currency. At imprv.ai, we treat art as training in unconventional thinking and the courage to express your own vision. We teach how to turn sensitivity into a design force that sets you apart in every area of life. Be the first to learn about courses that will teach you to create things that are unique, meaningful, and deeply human.'
            },
            skills: {
              label: 'Skills',
              introText: 'Your unique API to the world.',
              ctaText:
                'The modern world trains us to be consumers; we train creators and operators. This category is a tribute to resourcefulness and the hunger to master things that are difficult, beautiful, and useful. Reclaim your agency by mastering concrete tools and methods that let you operate beyond templates. Be the first when we launch diverse courses that prove your potential has no limits.'
            }
          },
          materialsAvailableFrom: 'Materials available from',
          availableAfter: 'Available after {{date}}',
          mapButtonText: 'map',
          comboGraphTitle: 'Map: {{title}}',
          couldNotReadGoals: 'Could not read goals',
          contentListTitle: 'Content',
          contentListShowMore: 'show all',
          contentListShowLess: 'hide',
          optionalNodeLabel: 'not included in final score',
          optionalGoalLabel: 'Progress not included in final score',
          comboGoalsHorizontalListHeader: 'This goal is also part of:',
          newContent: {
            marker: 'New',
            notice: 'New materials in the course — click to view'
          },
          sellingStatus: {
            preSale: 'PRE SALE',
            soldOut: 'SOLD OUT',
            new: 'NEW',
            startsOn: 'starts {{date}}'
          },
          landingPage: {
            previewVideo: {
              playButtonLabel: 'Play video',
              iframeTitle: 'Preview video'
            },
            preSaleBanner: {
              title: 'PRESALE',
              subtitle: 'Take advantage of the offer and sign up for the course at a lower price!'
            },
            soldOutBanner: {
              title: 'SOLD OUT',
              subtitle: 'This course is sold out. Check out other courses that may interest you!'
            },
            menu: {
              aboutMentor: 'Mentor',
              tableOfContents: 'Table of Contents',
              courses: 'Courses',
              pricing: 'Pricing',
              faq: 'FAQ',
              discord: 'Join us on Discord',
              share: 'Share'
            },
            discordBanner: {
              text: 'Join a community of <strong>over 4,000 people</strong> — and be the first to get discounts, free materials and mentor support.',
              cta: 'Join for free'
            },
            recommendations: {
              title: 'Recommended',
              subtitle: 'Below you will find a set of courses that may interest you.'
            },
            mentor: {
              female: {
                title: 'Mentor',
                leader: 'guide',
                introduction:
                  'You will be guided through the goal under the guidance of an excellent specialist'
              },
              male: {
                title: 'Mentor',
                leader: 'guide',
                introduction:
                  'You will be guided through the goal under the guidance of an excellent specialist'
              },
              many: {
                title: 'Mentors',
                leader: 'guides',
                introduction:
                  'You will be guided through the goal under the guidance of excellent specialists'
              },
              non_binary: {
                title: 'Mentor',
                leader: 'guide',
                introduction:
                  'You will be guided through the goal under the guidance of an excellent specialist'
              },
              dont_want_to_disclose: {
                title: 'Mentor',
                leader: 'guide',
                introduction:
                  'You will be guided through the goal under the guidance of an excellent specialist'
              }
            },
            faq: {
              haveQuestions: 'Maybe you have questions?',
              checkQuestions:
                'Check out the list of popular questions. Maybe you will find the answer here. If not, send an email to: ={{email}}=',
              sendEmail: 'Send email'
            },
            toc: {
              title: 'Learn the detailed =plan=',
              sectionTitle: 'Learn the detailed',
              sectionLeader: 'plan',
              sectionTitlePlaceholder: 'Learn the detailed',
              module: 'Module {{number}}'
            },
            keywords: {
              sectionTitle: 'What =you will learn='
            },
            pricing: {
              benefitsTitle: 'With the imprv® course you get:',
              whatIncludedTitle: "What's included?",
              forFree: 'Free course',
              onlineCourse: 'Online course',
              preSaleCTA:
                'Take advantage of the offer and sign up for the course at a lower price!',
              startDate: 'The course starts on',
              course: '/course',
              youSave: 'you save {{amount}} PLN ({{percentage}}%)',
              alreadyOwned: 'You already own this course'
            },
            editor: {
              hero: {
                titlePlaceholder: 'Goal title',
                shortDescriptionPlaceholder: 'Short description',
                ctaLabelPlaceholder: 'Button text',
                ctaSecondaryLabelPlaceholder: 'Button text',
                uploadImagePlaceholder: 'Change hero image',
                imagePlaceholder: 'Hero picture'
              },
              overview: {
                titlePlaceholder: 'Title',
                subtitlePlaceholder: 'Subtitle',
                descriptionPlaceholder: 'Description',
                benefitTitlePlaceholder: 'Benefit title',
                benefitItemPlaceholder: 'Benefit item',
                addItem: 'Add item',
                removeItem: 'Remove item'
              },
              faq: {
                questionPlaceholder: 'Question',
                answerPlaceholder: 'Answer',
                addFaq: 'question',
                removeFaq: 'Remove FAQ'
              },
              mentorBio: {
                sectionTitlePlaceholder: 'Section heading (colored)',
                introductionPlaceholder: 'Section introduction',
                namePlaceholder: 'Mentor name',
                titlePlaceholder: 'Title / role',
                bioPlaceholder: 'Bio',
                addMentor: 'mentor',
                removeMentor: 'Remove mentor'
              },
              modules: {
                titlePlaceholder: 'Module title',
                descriptionPlaceholder: 'Module description',
                addModule: 'module',
                removeModule: 'Remove module'
              },
              pricing: {
                titlePlaceholder: 'Section title',
                descriptionPlaceholder: 'Description',
                benefitPlaceholder: 'Benefit',
                includedTitlePlaceholder: 'Included title',
                includedItemPlaceholder: 'Included item',
                addBenefit: 'benefit',
                removeBenefit: 'benefit',
                addIncluded: 'benefit',
                removeIncluded: 'benefit'
              },
              socialProof: {
                titlePlaceholder: 'Section title',
                subtitlePlaceholder: 'Subtitle',
                statisticLabelPlaceholder: 'Label',
                featurePlaceholder: 'Feature text',
                addStatistic: 'statistic',
                removeStatistic: 'Remove statistic',
                addFeature: 'feature',
                removeFeature: 'Remove feature',
                iconPicker: {
                  trigger: 'Change icon',
                  icon: {
                    check: 'Checkmark',
                    star: 'Star',
                    heart: 'Heart',
                    person: 'Person',
                    cup: 'Trophy',
                    rocket: 'Rocket',
                    sparkles: 'Sparkles',
                    trending: 'Trending up'
                  }
                }
              },
              testimonials: {
                quotePlaceholder: 'Testimonial quote',
                authorPlaceholder: 'Author name',
                rolePlaceholder: 'Author role',
                addTestimonial: 'testimonial',
                removeTestimonial: 'Remove testimonial'
              },
              previewVideo: {
                title: 'Preview video',
                urlPlaceholder: 'Paste a YouTube or Vimeo link',
                titlePlaceholder: 'Video title (optional)',
                subtitlePlaceholder: 'Video subtitle (optional)',
                invalidUrl: 'Enter a valid YouTube or Vimeo link'
              },
              keywords: {
                title: 'Keywords',
                sectionTitlePlaceholder: 'What you will learn',
                keywordPlaceholder: 'Add a keyword',
                keywordInputLabel: 'Keyword',
                addKeyword: 'Add keyword',
                removeKeyword: 'Remove keyword'
              },
              visibility: {
                show: 'Show section on the page',
                hide: 'Hide section from the page',
                hiddenBadge: 'Hidden'
              },
              exitPrompt: {
                title: 'Unsaved changes',
                message: "You're about to leave with unsaved changes. Save them first?",
                save: 'Save',
                dismiss: 'Not now'
              },
              genaiSwitch: {
                label: 'Generate with AI'
              }
            }
          }
        },
        goalPackages: {
          landingPage: {
            pay: 'Pay',
            insteadOf: 'instead of',
            youSavePercentage: 'you save {{amount}} PLN ({{percentage}}%)',
            buyNow: 'Buy this pack now',
            buyPayU: 'Buy with PayU',
            buyStripe: 'Buy with Stripe',
            buyStripeHint: '(use Stripe coupon code)',
            processing: 'Processing…',
            mentorsTitle: 'Meet the mentors',
            mentorsIntro:
              'Learn under the guidance of experienced specialists who will guide you through every step.',
            pricingBenefitsTitle: 'With this pack you get:',
            pricingOnlineCourse: 'Online course',
            pricingWhatIncludedTitle: "What's included?",
            perPackageSuffix: '/pack',
            alreadyAcquired: 'You already own every course in this pack.',
            isFree: 'The pack has been added to your account.'
          }
        },
        goalSubscriptionVerification: {
          title: 'Completion certificate',
          startedAt: 'Started at',
          completedAt: 'Completed at',
          completedAtNotSetYet: 'In progress...',
          completedPercentage: 'Completed percentage',
          finalResult: 'Final result',
          currentResult: 'Current result',
          confirmInfix: 'confirms that',
          finishInfix: {
            MALE: 'finished the course',
            FEMALE: 'finished the course',
            NON_BINARY: 'finished the course',
            DONT_WANT_TO_DISCLOSE: 'finished the course'
          },
          mentorInfix: {
            MALE: 'mentor',
            FEMALE: 'mentor',
            NON_BINARY: 'mentor',
            DONT_WANT_TO_DISCLOSE: 'mentor'
          },
          downloadCertificate: 'Download certificate',
          stillInProgress: {
            text: 'The course is still in progress, the certificate is not yet available. If you want to update the data used for the certificate',
            linkText: 'click here'
          },
          buyItBanner: {
            text: 'Buy the course to get the certificate',
            CTAText: 'Buy the course'
          },
          errorBanner: {
            DELETED: {
              title: 'The course has been cancelled',
              description: 'The course has been cancelled. You can no longer access the content.'
            },
            CANCELLED: {
              title: 'The course has been cancelled',
              description: 'The course has been cancelled. You can no longer access the content.'
            }
          },
          newContentBanner: {
            title: 'New materials have appeared in the course',
            description:
              'Your certificate and score remain unchanged — new materials can only improve it.',
            CTAText: 'Go to the new materials'
          }
        },
        goalSubscriptions: {
          preSaleWaitInfo: {
            cta: 'Go to course home',
            titlePart1: 'The course is being prepared and will start from',
            titlePart2: 'as soon as we start you will be notified',
            tasksPlaceholder: 'Here you will find tasks to complete in the course'
          },
          chatTabTitle: 'Chat',
          goToTasks: 'Go to tasks',
          infoTabTitle: 'Info',
          settingsTabTitle: 'Settings',
          pending: {
            justAMoment: 'Just a moment...',
            processingPayment: 'Your payment is being processed',
            inProgress: 'Processing',
            redirection: 'You will be redirected in ',
            redirectionSeconds: '{{count}} seconds',
            paymentInProgress: 'Payment failed or is still being processed, please try again'
          },
          cancelled: {
            ohNo: 'Oh no!',
            sorryToSeeYouGo:
              "We're sorry you do not want to pursue this goal with us. We hope to see you again soon!"
          },
          cancelPayment: 'Cancel payment',
          externalSaleTitle: 'This goal is sold on an external platform',
          externalSaleDescription: "Go to the seller's cart",
          status: {
            pending: 'Pending',
            active: 'Active',
            paused: 'Paused',
            completed: 'Completed',
            cancelled: 'Cancelled',
            acquired: 'Acquired',
            accessGranted: 'Access granted'
          }
        },
        subscriptions: {
          accessDenied: {
            title: 'Goal has been cancelled',
            message: 'Access to content has been blocked'
          }
        },
        payments: {
          CTAs: {
            enrollReasonAcquiredAtImprv:
              'You have access to this course – you can start learning at any time.',
            enrollReasonAcquiredAtGoToIt:
              'The course was purchased on GoToIT, you can start learning at any time.',
            enrollReasonFree: 'The course is free, you can start learning at any time.',
            buyNow: 'Buy now',
            buyNowPayU: 'Pay with PayU (BLIK / transfer)',
            paid: 'Sign me up for the course',
            free: 'Sign me up for free',
            soldOut: 'Sold out, check out similar courses',
            processingFree: 'Enrolling, just a moment...',
            processingPayment: 'Your payment is being processed...',
            authorCannotEnroll:
              'As a mentor, you cannot pursue your own goals. Switch to an improver account or contact hello@imprv.ai',
            acquired: 'Acquired',
            enrollNow: 'Enroll now',
            loginAndEnroll: 'Log in to start learning',
            processing: 'Processing...',
            learnNow: 'Go to the course',
            programView: 'View program',
            login: 'Log in'
          },
          thankYou: {
            titlePart1: 'Congratulations! Access to the course',
            titlePart2: 'has been purchased',
            packageTitlePart1: 'Congratulations! Access to the package',
            packageTitlePart2: 'has been purchased',
            notEnrollable:
              'Something went wrong, are you already enrolled in the course? Let us know at help@imprv.ai',
            soldOut: 'This course is sold out. Check out other courses that may interest you!',
            backToHome: 'Back to home',
            processingOrder: 'Processing your order',
            imprvCoins: {
              title: 'Congratulations!',
              credited: 'Your Imprv Coins have been added to your account',
              creditedNeutral:
                'Payment completed – your Imprv Coins will appear in your history shortly',
              timeout:
                "The payment is taking longer than usual – your coins will appear in a few minutes. If they don't, write to help@imprv.ai",
              back: 'Go back'
            }
          },
          linkThankYou: {
            heading: 'Thank you for your payment',
            message: 'We have received your payment and will get back to you within 24 hours.',
            errorHeading: 'The payment did not go through',
            errorMessage: 'No charge has been made. You can try the payment again at any time.',
            retry: 'Try the payment again',
            exploreCourses: 'Browse other courses'
          },
          cancel: {
            titlePart1: 'You cancelled the payment for the course',
            packageTitlePart1: 'You cancelled the payment for the package',
            titlePart2: 'No charge has been made.',
            titlePart3: 'You can complete your purchase at any time.',
            goToGoal: 'Go to the course',
            goToPackage: 'Go to the package',
            backToHome: 'Back to home',
            loader: 'Loading...',
            imprvCoins: {
              title: 'The payment was cancelled',
              back: 'Go back'
            }
          }
        },
        assignments: {
          toc: {
            start: "Let's start!",
            end: 'End'
          },
          task: {
            navigation: {
              start: 'Start',
              previous: 'Previous',
              check: 'Check',
              submitForReview: 'Submit for Review',
              next: 'Next',
              tryAgain: 'Try Again',
              buyCoins: 'Top up',
              coinsWarning: {
                lowBalance_one: '<b>{{count}} coin</b> left',
                lowBalance_other: '<b>{{count}} coins</b> left',
                expiring_one: '<b>{{count}} coin</b> expires on <b>{{date}}</b>',
                expiring_other: '<b>{{count}} coins</b> expire on <b>{{date}}</b>',
                lowBalanceExpiring_one:
                  '<b>{{count}} coin</b> left, <b>{{expiring}}</b> {{verb}} on <b>{{date}}</b>',
                lowBalanceExpiring_other:
                  '<b>{{count}} coins</b> left, <b>{{expiring}}</b> {{verb}} on <b>{{date}}</b>',
                expiringVerb_one: 'expires',
                expiringVerb_other: 'expire',
                dismiss: 'Dismiss notice'
              }
            },
            toc: {
              start: 'Start',
              end: 'End',
              stageMap: 'STEPS MAP'
            }
          },
          missingUserInputToast:
            'Jump back to the steps marked with yellow color and fill in the missing user input',
          missingUserInputText: 'Please fill in the missing user inputs before finishing the task'
        },
        tasks: {
          blocks: {
            contentItem: {
              textInputPlaceholder: 'Enter text',
              uploadButtonText: 'Upload video, audio or image'
            }
          }
        },
        blocks: {
          inputText: {
            label: 'Your answer',
            placeholder: 'Enter text'
          },
          inputDatetime: {
            label: 'Your answer',
            day: '--',
            month: '---',
            year: '----',
            hour: '--',
            minute: '--',
            pickerHours: 'Hours',
            pickerMinutes: 'Minutes',
            months: [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec'
            ]
          },
          inputNumber: {
            label: 'Your answer',
            placeholder: 'Enter answer'
          },
          inputSingleChoice: {
            label: 'Your answer'
          },
          inputMultipleChoice: {
            label: 'Your answer'
          },
          inputBoolean: {
            label: 'Your answer'
          },
          inputRating: {
            label: 'Your answer'
          },
          inputFeedback: {
            checkingAnswer: 'Checking answer...',
            yourScore: 'Your score',
            correctAnswer: 'Correct answer',
            correctAnswerIs: 'The correct answer is',
            feedback: 'Feedback',
            seeHowYouDid: 'See how you did',
            nothingToAssessYet:
              'There is nothing to assess this time. Next time try writing even one sentence in your own words.'
          },
          inputFileUpload: {
            label: 'Your answer',
            afterUploadFeedback:
              'Your <strong>solution</strong> has been <strong>submitted for review</strong>. You will receive an email as soon as it is evaluated.',
            afterUploadFeedbackFailed:
              'We could not generate feedback for this submission due to a technical issue. Please use <strong>Try Again</strong> to resubmit your files.',
            addFeedback: 'add feedback',
            deleteFeedback: {
              header: 'Delete feedback',
              text: 'Are you sure you want to delete this feedback entry?'
            }
          },
          inputBricks: {
            title: 'Game Falling Bricks',
            start: 'Start Game',
            completed: 'Game completed!',
            score: 'Score: {{score}} %',
            perfect: 'Perfect! All bricks placed correctly!',
            wellDone: 'Well done!',
            goodJob: 'Good job! Keep practicing to improve your score.',
            keepPracticing: 'Keep practicing to improve your score.',
            tryAgain: 'PLAY AGAIN',
            previousResults: 'Show previous results',
            seeHowYouDid: 'See how you did',
            answeredCorrectly: 'You answered correctly',
            youSelected: 'You selected',
            noSelection: 'No category selected',
            notSelectedInTime: 'No category selected, check the correct one',
            correctAnswerIs: 'Correct answer',
            correctlySelectedCategory: 'Well done! Correct category selected',
            incorrectlySelectedCategory:
              'Unfortunately, you did not select the correct category, check the correct one',
            evaluationError: 'An error occurred while evaluating your results. Please try again.'
          },
          inputBlanks: {
            title: 'Game Fill Blanks',
            start: 'Start Game',
            completed: 'Game completed!',
            score: 'Score: {{score}} %',
            perfect: 'Perfect! All blanks filled correctly!',
            wellDone: 'Well done!',
            goodJob: 'Good job! Keep practicing to improve your score.',
            keepPracticing: 'Keep practicing to improve your score.',
            tryAgain: 'PLAY AGAIN',
            attempt: 'Attempt ',
            correctAnswer: 'Correct answer',
            hint: 'Hint',
            previousResults: 'Show previous results',
            seeHowYouDid: 'See how you did'
          },
          inputFlashcards: {
            title: 'Flashcards Game',
            completed: 'Game completed!',
            seeHowYouDid: 'See how you did',
            evaluationError: 'An error occurred while evaluating your results. Please try again.',
            slide: {
              startTitle: 'Start Flashcards',
              start: 'Start',
              check: 'Check',
              next: 'Next',
              retry: 'Try again',
              evaluationError: 'An error occurred while evaluating your answer. Please try again.',
              tryAgain: 'Try Again',
              yourAnswer: 'Type or dictate your answer here',
              hardExit: {
                title: 'Are you sure you want to exit?',
                body: 'Your progress in this attempt will be lost.',
                confirm: 'Exit',
                cancel: 'Back to game'
              },
              boolean: {
                true: 'Yes',
                false: 'No'
              }
            },
            editor: {
              questionPlaceholder: 'instruction',
              questionLabel: 'QUESTION',
              contentLabel: 'CONTENT',
              expectedAnswerLabel: 'EXPECTED ANSWER',
              addCard: 'Add card',
              editGame: 'Edit game',
              limitAnswerTime: 'Limit answer time',
              answerType: {
                text: 'Text',
                number: 'Number',
                date: 'Date',
                dateTime: 'Date and time',
                boolean: 'Yes/No'
              },
              text: {
                exactPlaceholder: 'Expected answer'
              },
              boolean: {
                true: 'Yes',
                false: 'No'
              }
            }
          },
          criteria: {
            sectionHeader: 'EVALUATION CRITERIA',
            addCriterion: 'add criterion',
            removeAllCriteria: 'Remove all criteria',
            titlePlaceholder: 'Criterion title',
            descriptionPlaceholder: 'Full description of the criterion',
            typeLabel: {
              REQUIRED: 'REQUIRED',
              OPTIONAL: 'OPTIONAL'
            },
            deleteCriterion: {
              header: 'Delete criterion?',
              text: 'This action cannot be undone.'
            },
            deleteAllCriteria: {
              header: 'Delete all criteria?',
              text: 'You will lose all defined evaluation criteria. This action cannot be undone.'
            }
          },
          agentFeedback: {
            title: 'SEE HOW YOU DID',
            loading: {
              text: 'AI is evaluating your answer...',
              longText: 'Taking longer than usual, AI is still working...'
            },
            error: {
              text: 'Something went wrong. Please try again.'
            }
          },
          attemptsHistory: {
            showPrevious: 'Show previous results',
            hidePrevious: 'Hide previous results'
          }
        },
        schedules: {
          scheduleInProgress:
            'Scheduling in progress... Updates may affect your inbox and calendar. Please wait.'
        },
        search: {
          noResults: 'No results found',
          moreResults: '... {{count}} more results',
          hideResults: 'hide results'
        }
      },
      // -- LEGACY BELOW
      units: {
        bpm: {
          short: 'bpm'
        },
        celsius: {
          short: '°C'
        },
        hrZone: {
          short: 'HR zone'
        },
        kcal: {
          short: 'kcal'
        },
        kilometer: {
          short: 'km'
        },
        kilometerPerHour: {
          short: 'km/h'
        },
        meter: {
          short: 'm'
        },
        meterPerSecond: {
          short: 'm/s'
        },
        mile: {
          short: 'mi'
        },
        minPerKm: {
          short: 'min/km'
        },
        percent: {
          short: '%'
        },
        rpm: {
          short: 'rpm'
        },
        second: {
          short: 's'
        },
        spm: {
          short: 'spm'
        },
        watt: {
          short: 'W'
        },
        yard: {
          short: 'yd'
        },
        repetitions: {
          short: 'reps'
        },
        wattZone: {
          short: 'Watt zone'
        },
        times: {
          single: 'time',
          plural: 'times'
        }
      },
      errorCodes: {
        generic: {
          header: 'Oops...',
          text: 'Something went wrong. Refresh the app or try again later. If the problem persists, please contact help@imprv.ai.'
        },
        GOAL__DELETE__HAS_SUBSCRIPTIONS: {
          header: 'You can not delete a goal that has active improvers',
          text: 'The goal you are trying to delete has active improvers, so it cannot be deleted.'
        },
        ORDER__GRANT__NOT_ALLOWED: {
          header: 'Access cannot be granted',
          text: 'You cannot grant access to yourself.'
        },
        ORDER__GRANT__ALREADY_HAS_ACCESS: {
          header: 'This person already has access',
          text: 'This person already has access to this goal.'
        },
        TASK__DELETE__USED_IN_STRATEGY: {
          header: 'You can not delete a task that is used in a strategy',
          text: 'The task you are trying to delete is used in a strategy, so it cannot be deleted.'
        },
        TAG__VALIDATION__TEXT_TOO_SHORT: {
          header: 'Tag name is too short',
          text: 'Tag name must be at least 1 characters long.'
        },
        TAG__VALIDATION__TEXT_TOO_LONG: {
          header: 'Tag name is too long',
          text: 'Tag name must be at most 100 characters long.'
        },
        TAG__VALIDATION__TEXT_ALREADY_EXISTS: {
          header: 'Tag already exists',
          text: 'Tag with that name already exists.'
        }
      },
      errors: {
        somethingWentWrong: 'Something went wrong'
      },
      activations: {
        editorTitle: "Activation rule's editor",
        tagNamePlaceholder: 'Tag name',
        tagChoiceTitle: 'Choose tag',
        taskChoiceTitle: 'Choose task',
        taskNamePlaceholder: 'Task name',
        ruleNamePlaceholder: 'Rule name',
        operatorChoiceTitle: 'Choose operator',
        valueChoiceTitle: 'Choose value',
        operators: {
          and: 'and',
          or: 'or'
        },
        ruleItemsLabel: {
          path: 'When and for whom should this path be activated?',
          task: 'When and for whom should this task be activated?',
          message: 'When and for whom should this message be displayed?',
          action: 'When and for whom should this action be activated?',
          group: 'When and for whom should this group be activated?'
        },
        ruleNameLabel: {
          other: 'How to name this rule?',
          path: 'How to name this path?'
        },
        addPhraseLabel: 'Add phrase',
        phraseTypes: {
          all: 'all',
          time: 'time',
          task: 'task',
          tag: 'tag',
          weather: 'weather',
          group: 'group'
        },
        errors: {
          ruleEmpty: 'Add name or rule'
        },
        actions: {
          delete: 'Delete'
        }
      },
      strategies: {
        publish: {
          button: 'Publish',
          publishCurrentTask: 'Publish current task',
          publishTasks: 'Publish all tasks',
          confirmCurrentTask: {
            message:
              "Before publishing, we'll check the task for issues. If we find any, we'll show them in the validation panel and pause the publish.",
            confirm: 'Check and publish'
          },
          confirmAllTasks: {
            message:
              "Before publishing all tasks, we'll check them for issues. If we find any, we'll show them in the validation panel and pause the publish.",
            confirm: 'Check and publish'
          }
        },
        verify: {
          button: 'Verify'
        },
        common: {
          save: 'Save'
        },
        editor: {
          title: 'Strategy editor',
          emptyState: 'Add a section to get started'
        },
        toc: {
          sectionNamePlaceholder: 'Section name',
          addTaskFromLibrary: 'task from library',
          addNewTask: 'new task',
          addSection: 'add section'
        },
        paths: {
          add: 'add section',
          startName: 'Start section',
          userDefinedNamePlaceholder:
            'Add rule to the path so that we know when and for whom it should be activated',
          rulelessPathNamePlaceholder: 'This path has no rule so it is invisible to users',
          emptyPathWarning: 'This path has no elements so it is invisible to users',
          endName: 'End section',
          actions: {
            markAsMultiActivated: 'Path can be activated multiple times',
            delete: 'Delete'
          }
        },
        nodes: {
          task: {
            name: 'Task',
            addTaskToPath: 'Add task to path',
            addTasksToPath: 'Add {{count}} tasks to section',
            selectedCount: '{{count}} selected',
            selectToAddToPath: 'Select tasks to add them to the path',
            done: 'Done'
          },
          actionMoveToPath: {
            title: 'Action Editor',
            name: 'Action',
            label: 'Move to path',
            placeholder: 'Choose path to move to',
            errors: {
              pathEmpty: 'Path cannot be empty',
              noNamedPathsToPointTo: 'There are no named paths to point to'
            }
          },
          messageTemplate: {
            title: 'Message Editor',
            name: 'Message',
            label: 'Message',
            placeholder: 'Enter message',
            errors: {
              messageEmpty: 'Message cannot be empty'
            }
          },
          actions: {
            preview: 'Preview',
            edit: 'Edit',
            markAsRequired: 'Mark as required',
            markAsNotRequired: 'Mark as not required',
            clone: 'Clone',
            delete: 'Delete'
          },
          group: {
            name: 'Group'
          },
          isRequired: {
            REQUIRED: 'REQUIRED',
            OPTIONAL: 'OPTIONAL'
          }
        }
      },
      goal: {
        defaultTitle: 'Here goes the title',
        defaultDescription: 'Here goes the description',
        defaultShortDescription: 'Here goes the short description',
        moreDetailsTitle: 'MORE DETAILS',
        tooltipAboutParentGoal: 'This goal is part of a COMBO goal'
      },
      goalPrice: {
        invalidAmountError: 'The amount can not be negative',
        couldNotSaveError: 'Could not save the price. Please adjust the values and try again',
        creatingPrice: 'Creating price...',
        publishing: 'Publishing...'
      },
      humanizedDate: {
        tomorrow: 'Tomorrow',
        today: 'Today',
        yesterday: 'Yesterday'
      },
      entities: {
        goal: 'GOAL',
        task: 'TASK',
        tasksLibrary: 'Tasks library',
        goalsLibrary: 'Goals library'
      },
      toasts: {
        linkCopied: 'Link copied',
        somethingWentWrong: 'Something went wrong',
        markedMessageAsAlreadyReplied: 'Marked message as "Already replied"',
        goalPublished: 'Goal published',
        goalPublishedStrategyFailed: 'Goal published, strategy failed to publish',
        strategyPublished: 'Strategy published',
        taskPublished: 'Task published',
        goalPricePublished: 'New price published',
        goalImageUpdated: 'Goal image updated',
        goalImageUpdateFailed: 'Failed to update the goal image',
        goalLandingPageUpdated: 'Goal landing page updated',
        goalLandingPageUpdateFailed: 'Failed to update the goal landing page',
        goalAlreadyEnrolled: "You're already enrolled in this goal — refreshing your access."
      },
      general: {
        yes: 'Yes',
        no: 'No',
        done: 'Done',
        close: 'Close',
        progress: 'Progress',
        loading: 'Loading',
        cancel: 'Cancel',
        confirm: 'Confirm',
        leaveGameConfirm: 'Are you sure you want to leave the game? Your progress will be lost.'
      },
      components: {
        dictation: {
          start: 'Record an answer',
          stop: 'Stop recording',
          microphoneBlocked:
            'The microphone is blocked. Allow access in your browser settings and reload the page.',
          microphoneNotFound: 'No microphone found.',
          microphoneAccessFailed: 'No microphone access',
          transcriptionFailed: 'Transcription failed'
        },
        reorderableList: {
          reorderItem: 'Drag to reorder'
        },
        transcriptDownloadButton: {
          label: 'Download transcript',
          filename: '{{lessonTitle}} – transcript',
          defaultFilename: 'transcript'
        },
        transcriptPendingNote: {
          message:
            'Hold off on generating further content until the transcript of the attached file is ready.'
        },
        transcriptReadyToast: {
          message: 'The video transcript is ready — you can carry on building your content.',
          openTask: 'Go to the task'
        },
        socialLinksEditor: {
          add: 'link',
          removeAriaLabel: 'Remove link',
          choosePlatform: 'Choose platform'
        },
        socialPlatforms: {
          INSTAGRAM: 'Instagram',
          FACEBOOK: 'Facebook',
          X: 'X',
          LINKEDIN: 'LinkedIn',
          YOUTUBE: 'YouTube',
          TIKTOK: 'TikTok',
          PINTEREST: 'Pinterest',
          SNAPCHAT: 'Snapchat',
          BEHANCE: 'Behance',
          GITHUB: 'GitHub',
          VIMEO: 'Vimeo',
          SOUNDCLOUD: 'SoundCloud',
          BLOG: 'Blog',
          WEBSITE: 'Website'
        },
        packagesFilterRow: {
          toggleLabel: 'packages'
        },
        tags: {
          title: 'Tags',
          editorSearchPlaceholder: 'Search or add a new tag',
          optionRename: 'Rename',
          optionDelete: 'Delete from everywhere',
          deleteConfirmationHeader: 'Are you sure you want to delete this tag?',
          deleteConfirmationText:
            'The tag will be removed. It will also be detached all task blocks'
        },
        deleteConfirmationBottomSheet: {
          confirmationButton: 'Yes',
          cancelButton: 'No'
        },
        imprvCoinsCounter: {
          ariaLabel: 'Imprv Coins balance',
          remainingLabel: 'You still have',
          unavailable: 'Balance unavailable'
        },
        goalPromoteButton: {
          publish: 'Publish',
          cannotPublish: 'You cannot publish this goal yet since it is not ready'
        },
        goalHubPublishButton: {
          publishAll: 'Publish all changes',
          publishGoalOnly: 'Publish goal changes',
          publishChanges: 'Publish changes',
          publishPriceFirst: 'Publish the price in the Monetization tab first',
          confirmAll: {
            message:
              'This will publish the goal thumbnail, title, description, price, landing page, all sections, and all its tasks. Continue?'
          },
          confirmGoalOnly: {
            message:
              'This will publish the goal thumbnail, title, description, price, landing page. Sections and all of its tasks will not be affected. Continue?'
          },
          validationBlocked_one:
            'This goal has {{count}} validation error. Fix it in the task editor before publishing.',
          validationBlocked_other:
            'This goal has {{count}} validation errors. Fix them in the task editor before publishing.'
        },
        goalEditorCard: {
          editButton: 'Edit'
        },
        publishGoalButton: {
          publishAll: 'Publish all changes'
        },
        createEntitiesMenu: {
          create: 'Create',
          createTask: 'Create task',
          createGoal: 'Create goal',
          cannotCreateForNow:
            "You cannot create tasks for now. We'll let you know when it's possible."
        },
        goalRecommendationBanner: {
          title: 'New courses already available',
          cta: 'Build your advantage'
        },
        dropdown: {
          errorMissingValue: 'Choose a value'
        },
        textInput: {
          linesUsed: 'lines {{current}}/{{max}}',
          maxLengthExceeded: 'The maximum length is {{maxLength}}'
        },
        fileInput: {
          dragHere: 'Drag and drop your file here',
          upload: 'Upload file',
          or: 'or',
          progress: 'Uploaded {{progress}}%',
          errorFileSize: 'The file is too big, the maximum size is {{maxSize}}',
          errorUpload: 'There was an error while uploading the file',
          errorInvalidType: 'Invalid file type',
          uploadButton: 'Upload files',
          cancel: 'Cancel'
        },
        attachmentCard: {
          defaultFileType: 'FILE',
          unnamedFile: 'Unnamed File',
          textPlaceholder: 'Add a description...'
        },
        numberInput: {
          errorWrongValue: 'Enter a number',
          errorWrongValueFromRange: 'Enter a number from {{min}} to {{max}}',
          errorNotMultiplicityOfStep: 'Enter a number that is a multiple of {{step}}'
        },
        blockAssertionCorrectAnswerEditor: {
          title: 'Correct answer',
          chooseCorrectAnswer: 'Choose correct answer',
          provideCorrectAnswer: 'Provide correct answer',
          passMessagePlaceholder: 'Enter a comment for the correct answer',
          failMessagePlaceholder: 'Enter a comment for the incorrect answer',
          add: 'Add correct answer',
          addDisabledNoChoices: "You can't add a correct answer without any choices",
          remove: 'Remove correct answer',
          errorInvalidCorrectAnswer: 'enter a valid correct answer'
        },
        blockAssertionEditor: {
          compareTypes: {
            EQUALS: 'equals',
            GREATER_THAN: 'above',
            LESS_THAN: 'below',
            GREATER_THAN_OR_EQUALS: 'greater or equals',
            LESS_THAN_OR_EQUALS: 'less or equals',
            BETWEEN: 'between',
            UNORDERED_EQUALS: 'equals',
            NOT_UNORDERED_EQUALS: 'not equals',
            CONTAINS: 'contains'
          },
          from: 'from',
          to: 'to',
          minGreaterThenMax: 'The minimum value cannot be greater than the maximum value'
        },
        blockInputText: {
          editor: {
            evaluationMode: {
              exactAnswer: 'Enter correct answer',
              criteria: 'AI evaluates by criteria'
            }
          },
          runtime: {
            feedback: {
              retry: 'Try again',
              newAttempt: 'NEW ATTEMPT'
            }
          }
        },
        timeInput: {
          errorWrongSecondsValue: 'Enter a number from {{min}} to {{max}}',
          errorWrongMinutesValue: 'Enter a number from {{min}} to {{max}}',
          errorWrongHoursValue: 'Enter a number from {{min}} to {{max}}',
          errorWrongValueNoMinMax: 'Enter a valid time from {{min}} to {{max}}',
          errorWrongValue: 'Enter a valid time'
        },
        paceInput: {
          errorWrongValueNoMinMax: 'Enter a valid pace from {{min}} to {{max}}',
          errorWrongSecondsValue: 'Enter a number from {{min}} to {{max}}',
          errorWrongMinutesValue: 'Enter a number from {{min}} to {{max}}',
          errorWrongValue: 'Enter a valid pace'
        },
        goalSubscriptionStatusChangeModal: {
          onSubscribed: {
            topMessage: "You've successfully enrolled to pursue your new goal.",
            bottomMessageStart:
              "From now on you'll be receiving task assignments and messages from this goal. You can find them in",
            bottomMessageConversation: 'Pursued goals',
            bottomMessageEnd: 'tab'
          },
          onCompleted: "You've successfully completed a goal!"
        },
        goalSubscriptionStrategyFinishedModal: {
          onCompleted: "You've successfully completed all your assignments for this goal!"
        },
        markAsIrrelevant: {
          text: 'Do you want to mark this message as irrelevant?'
        },
        estimatedTimeOfReply: {
          text: 'Estimated reply time'
        },
        replyTimeEstimation: {
          text: 'Checking when can we reply...'
        },
        conversations: {
          conversationInput: {
            respondingTo: 'Responding to',
            placeholder: 'Type or dictate your message',
            placeholderExtraDesktop: '(press shift + enter to add a new line)',
            disabledPlaceholder: 'Chat unavailable during the game',
            mentorbotThinkingPlaceholder: 'Mentorbot is thinking...',
            messageLengthExceeded: 'Message is too long'
          },
          coinsNotice: {
            message: 'You have run out of Imprv Coins. Top up to keep chatting with Mentorbot.',
            buyCta: 'Buy more Imprv Coins',
            lowBalance_one:
              '<b>{{count}} Imprv Coin</b> left. Top up to keep chatting with Mentorbot.',
            lowBalance_other:
              '<b>{{count}} Imprv Coins</b> left. Top up to keep chatting with Mentorbot.',
            expiring_one:
              '<b>{{count}} Imprv Coin</b> expires on <b>{{date}}</b>. Use it with Mentorbot.',
            expiring_other:
              '<b>{{count}} Imprv Coins</b> expire on <b>{{date}}</b>. Use them with Mentorbot.',
            lowBalanceExpiring_one:
              '<b>{{count}} Imprv Coin</b> left, <b>{{expiring}}</b> of them {{verb}} on <b>{{date}}</b>. Top up to keep chatting with Mentorbot.',
            lowBalanceExpiring_other:
              '<b>{{count}} Imprv Coins</b> left, <b>{{expiring}}</b> of them {{verb}} on <b>{{date}}</b>. Top up to keep chatting with Mentorbot.',
            expiringVerb_one: 'expires',
            expiringVerb_other: 'expire',
            dismiss: 'Dismiss notice'
          },
          conversationTasks: {
            noNewTasks: 'There are no new tasks for you yet, but they will appear soon',
            noNewTasksSincePaused: 'The goal is paused, so there are no new tasks for you'
          },
          conversationTopic: {
            whyWeAreHereSubscription: 'We are here to help you achieve your goal',
            whyWeAreHereGoal: 'We are here to build and manage the goal',
            info: {
              author: 'Author',
              subscriptionStartedDate: 'Start date',
              subscriptionEstimatedCompleteDate: 'Estimated completion date',
              subscriptionEstimatedCompleteDateMissing: 'Missing',
              subscriptionCost: 'Cost',
              subscriptionCostFree: 'Free'
            },
            participants: {
              title: 'Participants'
            },
            settings: {
              title: 'Settings',
              paymentHistory: 'Payment history',
              pauseSubscription: 'Suspend',
              resumeSubscription: 'Resume',
              goToGoalPage: 'Go to goal page',
              shareGoal: 'Share goal',
              unsubscribe: 'Resign'
            }
          },
          queue: {
            tooltipQueued: 'Sending in a moment...',
            tooltipSending: 'Sending...',
            tooltipFailed: 'Failed to send. Tap to retry.'
          },
          outcomeNotice: {
            failed: 'Something went wrong and I could not reply. Please try again.',
            recommended: 'I have prepared a response — it is now awaiting your mentor’s review.',
            irrelevant: 'I could not help with that. Please try rephrasing your question.'
          },
          cancellingNotice: 'Cancelling…'
        },
        buyImprvCoinsModal: {
          bonus: '+{{percent}}% more coins',
          badgePopular: 'Most popular',
          starterCaption: 'To get started',
          title: 'Buy Imprv Coins',
          subtitle:
            'You spend Imprv Coins on Mentorbot replies and other AI features. Purchased coins never expire.',
          cta: 'Buy for {{price}}',
          vatInfo: 'Price includes VAT',
          error: "We couldn't start the payment – please try again",
          packageUnavailable: 'This package is no longer available – refresh the app and try again',
          fetchError: "We couldn't load the packages",
          retry: 'Try again'
        },
        conversationMessage: {
          inResponseTo: 'In response to',
          cancelReply: 'Stop reply'
        },
        aiGeneratedBadge: {
          label: 'AI',
          info: 'This response was generated by artificial intelligence (Imprv AI).'
        },
        conversationMessageAttachments: {
          sources: 'Sources:'
        },
        conversationMessageResponseRecommendation: {
          recommendation: 'Response recommendation',
          counter: '{{current}} / {{total}}',
          visibilityInformation: 'This recommendation is visible only to you',
          sendButton: 'Send',
          editButton: 'Edit',
          rejectButton: 'Reject',
          expand: 'more',
          collapse: 'collapse'
        },
        mentorbotThinking: {
          verbs: [
            'Thinking',
            'Analyzing',
            'Preparing response',
            'Considering',
            'Processing',
            'Reviewing context',
            'Formulating answer',
            'Searching for information',
            'Evaluating options',
            'Crafting a reply'
          ]
        },
        goalActions: {
          published: {
            label: 'Published'
          },
          draft: {
            label: 'Draft'
          },
          edit: {
            label: 'Edit goal'
          },
          like: {
            label: 'Like goal'
          },
          unlike: {
            label: 'Unlike goal'
          },
          share: {
            label: 'Share goal'
          },
          more: {
            label: 'More actions'
          }
        },
        goalBottomSheet: {
          pauseGoal: 'Pause goal',
          goToGoalEditor: 'Go to goal editor',
          goToSkillGraphEditor: 'Open skill graph',
          goToGoalUsers: 'Go to goal users',
          deleteGoal: 'Delete goal',
          unsubscribeGoal: 'Resign',
          subscribeGoal: 'I want to achieve this goal',
          resumeGoal: 'Resume goal',
          deleteGoalConfirmationHeader: 'Are you sure you want to delete this goal?',
          deleteGoalConfirmationText:
            'This action is irreversible. After you delete the goal, you will not have access to it anymore'
        },
        markdownEditor: {
          expand: 'Expand',
          done: 'Done'
        },
        skillGraphEditor: {
          back: 'Back',
          editNode: 'Edit node',
          label: 'Label',
          labelPlaceholder: 'Node label…',
          description: 'Description',
          descriptionPlaceholder: 'Add a description…',
          autoLayout: 'Auto Layout',
          generate: 'Generate',
          save: 'Save',
          saving: 'Saving...',
          saved: 'Saved!',
          saveError: 'Error!',
          publish: 'Publish',
          publishing: 'Publishing...',
          published: 'Published!',
          publishError: 'Publish failed',
          loading: 'Loading graph...',
          loadError: 'Failed to load skill graph'
        },
        goalUsersListPage: {
          noSubscriptions: 'No users',
          filters: {
            all: 'all',
            notStarted: "didn't start",
            barelyStarted: 'barely started',
            hardMaterial: 'hard material',
            best: 'best',
            accessGranted: 'got access'
          },
          addUser: {
            button: 'User',
            title: 'Grant access',
            description:
              'Enter an email address. This person gets the same access as after a purchase – even if the goal is private. Access works once they sign in to the account with this address.',
            placeholder: 'Email address',
            submit: 'Add',
            added: 'Added {{email}}',
            noNotificationNote:
              'We do not send a notification – let this person know the course is available after signing in.',
            publishFirstToast: 'Publish the goal first',
            errors: {
              invalidEmail: 'Enter a valid email address.'
            }
          },
          pendingCard: {
            waiting: 'Waiting to start'
          },
          grantsLoadFailedToast: 'Could not read grants'
        },
        messagesView: {
          closeConversation: 'Close conversation',
          conversationError: "Couldn't load the conversation",
          doneSection: 'DONE',
          emptyState: 'No messages',
          markDone: 'Mark as done',
          markPending: 'Mark as not done',
          pendingRecommendation: 'Recommendation awaiting your approval',
          openBeforeDone: 'Review the conversation before marking it done',
          openGoal: 'Open goal',
          openGoalUser: 'Open member in goal',
          pendingRecommendationConfirmTitle: 'Mark as done?',
          pendingRecommendationConfirmBody:
            "It looks like the mentorbot's suggested reply is still waiting for your confirmation. Are you sure you want to mark this conversation as done?",
          pendingRecommendationConfirmProceed: 'Mark anyway',
          pagination: {
            range: '{{from}}–{{to}} of {{total}}',
            empty: '0 of 0',
            prev: 'Previous page',
            next: 'Next page'
          },
          inboxFilter: {
            trigger: 'Filter',
            tabs: {
              user: 'User',
              goal: 'Goal'
            },
            searchUser: 'Search user',
            searchGoal: 'Search goal',
            noResults: 'No results',
            clearAll: 'Clear all'
          }
        },
        goalUserSelector: {
          selectUser: 'Select user',
          searchPlaceholder: 'Search user...',
          noResults: 'No users found'
        },
        goalUnsubscribeModal: {
          title: "You're about to stop pursuing this goal",
          textWhenActive:
            'Resigning from pursuing this goal will mean losing all of your progress so far. Are you sure?',
          textWhenPaused:
            'Resigning from pursuing this goal will mean losing all of your progress so far. Are you sure?',
          closeButton: 'Close'
        },
        goalSubscriptionButtons: {
          subscribe: 'Enroll',
          subscriptionCost: 'Price',
          subscriptionCostFree: 'Free',
          subscriptionCostRecurring: 'per month'
        },
        goToGoalSubscriptionButton: {
          goToSubscription: 'Go to pursued goals'
        },
        goalCardUsers: {
          completedBy: 'Completed by {{count}} users'
        },
        mentorGoalCard: {
          activeChip: 'Active',
          activeChipTooltip: 'Active subscribers',
          changed: 'Added or changed',
          errors: 'Errors',
          removed: 'Removed',
          warnings: 'Warnings'
        },
        goalSampleTasks: {
          title: 'SAMPLE TASKS'
        },
        goalStatistics: {
          author: {
            label: 'Author:'
          }
        },
        master: {
          name: 'Imprv AI',
          jobTitle: 'Your personal assistant',
          about:
            "I'm here to help you achieve your goals. I can answer your questions, assign tasks, and remind you about them. I can also assist you in managing your goals and tracking their progress. I'm still learning, so please be patient with me."
        },
        noActiveSubscriptionForGoal: {
          header: 'You are not enrolled in this goal',
          text: 'Please enroll to start receiving tasks.',
          goToGoalButton: {
            text: "Go to goal's page"
          }
        },
        errorUnexpectedPlaceholder: {
          header: 'Oops...',
          reload: {
            text: 'Something went wrong. Refresh the page or try again later',
            buttonText: 'Refresh the page'
          },
          backToHome: {
            text: 'Something went wrong. Go back to the homepage.',
            buttonText: 'Back to homepage'
          }
        },
        errorUnauthorizedPlaceholder: {
          header: 'Oops...',
          text: 'You are not authorized to view this page.',
          backToHomeButton: {
            text: 'Back to homepage'
          }
        },
        errorNotFoundPlaceholder: {
          header: 'Oops...',
          text: 'The page you are looking for does not exist.',
          backToHomeButton: {
            text: 'Back to homepage'
          }
        },
        goalUnavailablePlaceholder: {
          header: "We can't display this page",
          signedOutText:
            'Log in with an account that may have access, or ask the person who shared the link for access.',
          signedInText:
            'If you have another account that may have access, switch accounts. You can also ask the person who shared the link for access.',
          loginButton: {
            text: 'Log in'
          },
          switchAccountButton: {
            text: 'Switch account'
          }
        },
        placeholderNoProfileAboutMe: {
          text: 'There is no description yet'
        },
        placeholderNoProfileLinks: {
          text: 'There are no links to social media and other websites yet'
        },
        placeholderNoCompletedGoals: {
          textWithWho: "{{who}} didn't complete any goals yet.",
          text: "You don't have any completed goals yet.",
          exploreGoalsButton: {
            text: 'Explore goals'
          }
        },
        placeholderNoConversationMessages: {
          text: "This conversation doesn't have any messages yet"
        },
        placeholderNoConversations: {
          text: 'When you will enroll to a new goal, a new chat will apprear here.'
        },
        placeholderNoConversationTasks: {
          text: "You don't have any tasks yet"
        },
        placeholderNoCreatedGoals: {
          textWithWho: "{{who}} didn't create any goals yet.",
          text: "You don't have any created goals yet.",
          createButton: {
            text: 'Create your own goal'
          },
          placeholder: 'This option is not available yet.'
        },
        placeholderNoLikedGoals: {
          text: "You don't have any liked goals yet.",
          exploreGoalsButton: {
            text: 'Explore goals'
          }
        },
        placeholderNoNotifications: {
          text: "You don't have any notifications yet."
        },
        placeholderNoStreams: {
          exploreGoalsButton: {
            text: 'Explore goals'
          }
        },
        placeholderNoSubscribedGoals: {
          text: 'You do not pursue any goals yet.',
          exploreGoalsButton: {
            text: 'Explore goals'
          }
        },
        placeholderNothingFound: {
          text: 'Ups... nothing found'
        },
        progressLegend: {
          title: 'PROGRESS BAR',
          label: 'This is the progress of your goal:',
          entities: {
            done: 'This represents completed tasks.',
            todo: 'This indicates tasks to be done.',
            pending:
              'This represents tasks that are active now and are currently shown on your homepage.',
            cancelled: 'This represents tasks that have been cancelled.'
          },
          closeButton: {
            text: 'Close'
          }
        },
        mainMenu: {
          links: {
            explore: 'Explore',
            create: 'Create',
            collapse: 'Collapse'
          }
        },
        missingFeature: {
          text: "{{what}} doesn't exist yet"
        },
        searchBar: {
          placeholder: 'Search',
          disabledPlaceholder: 'Search unavailable during the game'
        },
        chatbotEntryEditor: {
          qa: {
            question: 'Question',
            answer: 'Answer'
          },
          confirmButton: {
            text: 'Save'
          }
        },
        chatbotSettingsEditor: {
          responseThreshold: {
            label: 'How much should the Chatbot help you answer the questions?'
          },
          alarmPhrases: {
            label: 'Write phrases which will mark messages as important'
          }
        },
        chatbotSettingsPhraseInput: {
          newPlaceholder: "Write a prase (ie. I don't feel well)",
          deleteButton: {
            text: 'Delete'
          }
        },
        affiliateLink: {
          label: 'Your affiliate link',
          copy: 'Copy affiliate link',
          copied: 'Copied!'
        },
        earningsRange: {
          currentMonth: 'This month',
          lastMonth: 'Last month',
          last6Months: 'Last 6 months'
        },
        navBar: {
          homepage: 'Homepage',
          subscriptions: 'Pursued goals',
          create: 'Create',
          explore: 'Explore',
          messages: 'Messages',
          profile: 'My profile',
          notifications: 'Notifications',
          tasks: 'Tasks library',
          goals: 'Goals library',
          earnings: 'Earnings',
          moreBottomSheetTitle: 'Go to'
        },
        taskActionsBottomSheet: {
          editTask: 'Edit task',
          deleteTask: 'Delete task',
          cloneTask: 'Clone task',
          cloneTaskPrefix: '(Copy)',
          deleteConfirmationHeader: 'Are you sure you want to delete this task?',
          deleteConfirmationText:
            'This action is irreversible. After you delete the task, you will not have access to it anymore'
        },
        deleteErrorBottomSheet: {
          acknowledgeButton: 'I understand'
        }
      },
      pages: {
        affiliate: {
          title: 'Your earnings',
          header: 'Affiliate earnings',
          subtitle:
            'Commission on everything you sell and every lead you brought – direct and assisted.',
          empty: {
            title: 'No leads yet',
            description:
              'When someone you brought buys through your link, their orders show up here.'
          },
          error: {
            title: 'Unable to load earnings',
            description: 'Something went wrong while loading your earnings. Please try again.'
          },
          summary: {
            title: 'Summary',
            leads: 'Leads brought',
            orders: 'Orders'
          },
          chart: {
            title: 'Leads over time',
            leads: 'Leads'
          },
          table: {
            title: 'Orders from your leads',
            date: 'Date and time',
            buyer: 'Buyer',
            orderId: 'Order ID',
            kind: 'Kind',
            product: 'Product',
            landingPage: 'Landing page',
            openLandingPage: 'Open',
            kindFirst: 'First order',
            kindNext: 'Next order',
            typeGoal: 'Goal',
            typePackage: 'Package'
          }
        },
        garminConnections: {
          title: 'Garmin Connections',
          loaderText: 'loading your garmin connections',
          addNew: 'Add new Garmin Connection',
          yourAreConnected: 'You are connected with Garmin',
          yourConnectionIsPending: 'Your connection with Garmin is pending',
          noActiveOrPending: 'You have no Garmin connections'
        },
        garminConnectionAuthorize: {
          loaderText: 'authorizing your garmin connection',
          authorizedText: 'Your Garmin Connection was authorized'
        },
        account: {
          yourAccount: 'Your account',
          tabs: {
            settings: 'Settings',
            earnings: 'Monetization',
            orders: 'Orders'
          },
          orders: {
            header: 'Your orders',
            tableTitle: 'Order history',
            tableProduct: 'Product',
            tableDate: 'Date',
            tableAmount: 'Amount',
            tableStatus: 'Status',
            typeGoal: 'Goal',
            typePackage: 'Package',
            typeCoins: 'IMPRV Coins',
            coins: 'IMPRV Coins',
            status: {
              active: 'Active',
              refund: 'Refunded',
              expired: 'Expired'
            },
            emptyTitle: 'No orders yet',
            emptyDescription: 'Goals and packages you buy will appear here.',
            invoice: {
              column: 'Invoice',
              button: 'Get invoice',
              pending: 'Your invoice is being generated. Please try again in a moment.',
              missingDetails: 'Add your invoice details in Account settings first.',
              failed: "We couldn't issue the invoice – please check your invoice details (NIP).",
              detailsRequiredBanner: 'To download invoices, add your invoice details first.',
              detailsRequiredLink: 'Add invoice details'
            }
          },
          mentorEarnings: {
            header: 'Mentor earnings',
            summaryTitle: 'Summary',
            chartTitle: 'Payout over time',
            tableTitle: 'Sold goals',
            netTotal: 'Net payout',
            grossTotal: 'Gross revenue',
            unitsSold: 'Units sold',
            chartNet: 'Net payout',
            tableDate: 'Date',
            tableGoal: 'Goal',
            tableBuyer: 'Buyer',
            tableGross: 'Paid',
            tableNet: 'Your payout',
            emptyTitle: 'No sales yet',
            emptyDescription: 'When improvers buy your goals, your earnings will appear here.',
            errorTitle: 'Unable to load earnings',
            errorDescription: 'Something went wrong while loading your earnings. Please try again.'
          },
          garminConnectionsButton: {
            text: 'Garmin Connections'
          },
          usages: {
            certification: 'Field used in certificate'
          },
          sections: {
            affiliatePercentages: {
              title: 'Your percentages',
              referralCode: {
                title: 'Referral code',
                hint: 'Add ?ref= with this code to any goal or package link.',
                copy: 'Copy',
                copied: 'Copied'
              },
              rates: {
                title: 'Commission rates',
                firstDirect: 'Direct – first purchase',
                firstAssisted: 'Assisted – first purchase',
                residualDirect: 'Direct – later purchases',
                residualAssisted: 'Assisted – later purchases',
                mentorShareTitle: 'Your mentor share',
                mentorMarginIndependent: 'Independent margin',
                mentorMarginAssisted: 'Assisted margin',
                mentorMarginHint:
                  'Your share of the goal price: independent when you sell it yourself, assisted when an affiliator/imprv drove the sale – applied automatically in the goal price editor.',
                notSet: '–'
              },
              explanation:
                '"Direct" applies when a buyer arrives through your own link; every other purchase by a lead you brought earns the assisted rate. Terms are set by imprv.'
            },
            loginInfo: {
              title: 'Login info',
              editors: {
                login: {
                  title: 'Login',
                  loginMethod: 'You log in with:',
                  logoutButtonText: 'Logout',
                  loginMethods: {
                    PASSWORD: 'Email and password',
                    GOOGLE: 'Google Account'
                  }
                }
              }
            },
            invoice: {
              header: 'Invoice details',
              description:
                'Used to issue invoices for your purchases. Required only when you request an invoice.',
              editors: {
                buyerType: 'Buyer type',
                buyerTypeOptions: {
                  COMPANY: 'Company',
                  PRIVATE: 'Private person'
                },
                name: 'Name / company name',
                namePlaceholder: 'e.g. ACME Sp. z o.o. or John Smith',
                taxId: 'Tax ID (NIP)',
                taxIdPlaceholder: 'e.g. 1234563218',
                taxIdError: 'Invalid NIP',
                street: 'Street',
                streetNumber: 'Building / unit number',
                city: 'City',
                postCode: 'Post code',
                country: 'Country',
                countryPlaceholder: 'PL',
                tooLongError: 'Value is too long'
              }
            },
            accountInfo: {
              title: 'Account info',
              editors: {
                account: {
                  email: 'Email',
                  emailInfo:
                    'Currently we do not support changing the email. If you have any questions, please contact us at hello@imprv.ai',
                  username: 'Username',
                  usernameInfo:
                    'Currently we do not support changing the username. If you have any questions, please contact us at hello@imprv.ai',
                  firstName: 'First name',
                  firstNamePlaceholder: 'Enter first name',
                  firstNameMaxLengthError: 'First name must be less than 200 characters',
                  lastName: 'Last name',
                  lastNamePlaceholder: 'Enter last name',
                  lastNameMaxLengthError: 'Last name must be less than 200 characters',
                  gender: 'Gender',
                  genderOptions: {
                    MALE: 'Male',
                    FEMALE: 'Female',
                    NON_BINARY: 'Non-binary',
                    DONT_WANT_TO_DISCLOSE: "Don't want to disclose"
                  },
                  genderInfo:
                    'We will use this information only to better address you (e.g. pronouns). Providing it is optional — if you prefer not to disclose it, select "Don\'t want to disclose"',
                  saveButtonText: 'Save'
                }
              }
            },
            imprvCoins: {
              title: 'Imprv Coins history',
              empty: 'No imprv coins history yet',
              loadFailed: 'Could not load your imprv coins history – please refresh the page',
              dataIncomplete:
                'Some imprv coins data is temporarily unavailable – the history below is incomplete and the balance is not shown',
              columns: {
                number: 'Number',
                description: 'Description',
                date: 'Date',
                expiry: 'Valid until'
              },
              acquisitionKinds: {
                PURCHASE: 'Purchase',
                BONUS: 'Bonus'
              },
              requestKinds: {
                run: 'Generator',
                transcription: 'Dictation',
                image_edit: 'Image editing',
                mentorbot_reply: 'Mentorbot reply',
                block_evaluation: 'Answer evaluation',
                unknown: 'AI usage'
              },
              perpetual: 'no expiry',
              summary: {
                remaining: 'Remaining',
                byExpiry: 'Valid until',
                perpetual: 'no expiry'
              },
              buyCta: 'Buy coins'
            }
          }
        },
        assignments: {
          loadingConversation: 'Loading conversation...'
        },
        accountProfile: {
          createdGoals: 'Created goals',
          completedGoals: 'Completed goals',
          joinedAt: 'Joined at',
          aboutMe: 'About me',
          moreInfo: {
            title: 'More info'
          }
        },
        chatbots: {
          editor: {
            title: 'Chatbot editor',
            trainingTabTitle: 'Training',
            settingsTabTitle: 'Settings',
            testingTabTitle: 'Test',
            question: 'Question',
            questionPlaceholder: 'Write a question',
            answer: 'Answer',
            generateResponse: 'Generate response',
            generatingResponse: 'Response is being processed...',
            thanksForFeedback: 'Thank you for your feedback!',
            howDoYouRateTheResponse: 'How do you rate the response of the Chatbot?',
            clear: 'Clear',
            chatbotCouldNotRespond:
              'The data available in the "Training" tab is not enough is not enough for the Chatbot to respond to this question. If user would ask such question it would be redirected to you',
            addButton: {
              text: 'Add new question'
            },
            actions: {
              delete: 'Delete question'
            }
          }
        },
        conversations: {
          states: {
            paused: {
              title: 'Goal paused',
              resumeButton: {
                text: 'Resume'
              }
            },
            completed: {
              title: 'This conversation was archived since to the goal was completed'
            }
          }
        },
        goalEditorHub: {
          basicInfo: {
            title: 'Basic information'
          },
          landingPage: {
            title: 'Landing Page',
            empty: 'Update landing page'
          },
          tasks: {
            title: 'Tasks',
            summary: '{{paths}} sections and {{tasks}} tasks'
          },
          tabs: {
            content: 'Content',
            monetization: 'Monetization',
            users: 'Users',
            visibility: 'Visibility'
          },
          monetization: {
            priceSectionTitle: 'Price',
            couponsSectionTitle: 'Coupons & discounts',
            salesSectionTitle: 'Sales',
            earnings: {
              summaryTitle: 'Summary',
              netTotal: 'Net earnings',
              grossTotal: 'Gross revenue',
              unitsSold: 'Units sold',
              chartTitle: 'Net earnings over time',
              chartNet: 'Net',
              tableTitle: 'Sales',
              tableDate: 'Date',
              tableBuyer: 'Buyer',
              tableGross: 'Paid',
              tableNet: 'Your payout',
              emptyTitle: 'No sales yet',
              emptyDescription: 'When learners buy this goal, your earnings will show up here.',
              errorTitle: 'Unable to load earnings',
              errorDescription: 'Something went wrong while loading this data. Please try again.'
            },
            priceCardTitle: 'COURSE PRICE',
            priceForImproverLabel: 'Price visible to improver',
            amountForMentorLabel: 'Net for mentor',
            amountForMentorSaleModeTitle: 'Sale type',
            amountForMentorDirect: 'Direct sale',
            amountForMentorAssisted: 'Assisted sale',
            feesLabel: 'Margin and taxes',
            publishChanges: 'Publish changes',
            createPrice: 'Create price'
          },
          visibility: {
            cardTitle: 'GOAL VISIBILITY',
            publicLabel: 'Public',
            statusPrivate:
              'The goal is private – only people with access can see it. It does not appear in Explore.',
            statusPublic:
              'The goal is public – anyone with the link can open its page. The goal is visible in Explore.',
            lockedHint: 'Making goals public is currently available to selected accounts.',
            makePublicModal: {
              title: 'Make the goal public?',
              body: 'The goal will appear in Explore and anyone with the link will see its page. The change takes effect when you publish the goal.',
              confirm: 'Make public'
            },
            makePrivateModal: {
              title: 'Hide the goal?',
              body: 'The goal will disappear from Explore and new people will lose access to its page. Current participants keep their access. The change takes effect when you publish the goal.',
              confirm: 'Hide goal'
            },
            toastPublic: 'Saved – the goal becomes public on publish',
            toastPrivate: 'Saved – the goal becomes private on publish',
            effectiveOnPublish: 'Visibility changes take effect when the goal is published.',
            loadFailed: 'We could not load the page visibility settings.',
            pendingPublishHint:
              'The goal visibility change awaits publishing – landing page access follows the published version.',
            landingPageAccess: {
              cardTitle: 'GOAL PAGE VISIBILITY',
              dropdownTitle: 'Who can open the goal page?',
              publicForced: 'Everyone – the goal is public',
              publicHint: 'A public goal is also listed in Explore.',
              immediateNote: 'Changes here apply immediately, without publishing.',
              toastSaved: 'Page visibility saved.',
              toastSavedListsKept:
                'Page visibility saved. Saved access lists are kept and resume working when you switch back.',
              options: {
                NOBODY: 'No one – page hidden',
                SELECTED: 'Selected people and domains',
                ANYONE: 'Anyone with the link'
              }
            }
          },
          accessList: {
            title: 'ACCESS LIST',
            description:
              'The goal page opens for anyone whose email address is on the list – or whose address is in one of these domains.',
            emailsGroupLabel: 'Email addresses',
            domainsGroupLabel: 'Domains',
            immediateNote: 'Changes here apply immediately, without publishing.',
            inputPlaceholder: 'Add an email address or a domain, e.g. acme.com…',
            addLabel: 'Add',
            removeLabel: 'Remove {{value}}',
            invalidEntry: 'Enter an email address or a domain.',
            duplicateEntry: 'Already on the list.',
            toastSaved: 'Access list saved.'
          }
        },
        goalEditor: {
          title: 'Goal editor',
          imageField: {
            text: 'thumbnail',
            uploadButtonText: 'Upload thumbnail'
          },
          titleField: {
            text: 'goal name',
            placeholder: 'Enter goal name'
          },
          shortDescriptionField: {
            text: 'short description',
            placeholder: 'Enter short description'
          },
          descriptionField: {
            text: 'description',
            placeholder: 'Enter description'
          },
          categoriesField: {
            text: 'categories'
          },
          yamlField: {
            text: 'Landing Page',
            buttonText: 'Edit',
            saveButtonText: 'Save changes'
          },
          chatbotField: {
            text: 'chatbot',
            summary: 'You have currently {{count}} Q&A pairs',
            summaryDraft: 'You have currently {{count}} draft Q&A pairs which are unpublished',
            validationError: 'You need at least {{count}} Q&A pairs',
            publishLabel: 'Publish draft version of chatbot to already published goal'
          },
          strategyField: {
            text: 'strategy',
            summary: 'You have currently {{count}} tasks in your strategy',
            summaryDiff: 'You have currently the following unpublished changes:',
            summaryPathsDraft: '{{count}} x changed or added paths',
            summaryPathsToDelete: '{{count}} x paths to be deleted',
            summaryNodesToDelete: '{{count}} x nodes to be deleted',
            summaryTasksDraft: '{{count}} x changed or added tasks',
            summaryTaskBlocksDraft: '{{count}} x changed or added task blocks',
            summaryActivationRulesDraft: '{{count}} x changed or added activation rules',
            validationError: 'You need at least {{count}} tasks in your strategy',
            publishLabel: 'Publish draft version of strategy to already published goal'
          }
        },
        home: {
          header: 'Homepage',
          welcome: 'Welcome {{name}}!',
          welcomeSubtext:
            'Shall we continue learning? Today we have a lot ahead of us, and there will be even more!',
          yourGoalsTab: 'Your goals ({{count}})',
          messagesTab: 'Messages ({{count}})',
          noActiveGoalSubscriptions: {
            header: "Currently, you don't pursue any goals",
            button: 'Find next goal to pursue'
          },
          neverHadActiveGoalSubscriptions: {
            header: 'Welcome to Imprv👋',
            text: 'Find your first goal',
            button: 'Find next goal to pursue'
          },
          goalSubscriptionsArePaused: {
            header: 'Your goals are paused ☕',
            text: 'Resume your goals to start receiving tasks again',
            button: 'Go to your goals'
          }
        },
        taskEditor: {
          title: 'Task editor',
          preview: 'Preview',
          addTaskButton: {
            text: 'Add task'
          },
          addBlockButton: {
            text: 'new block'
          },
          addBlockGroupButton: {
            text: 'Add block series'
          },
          createBlock: {
            text: 'Create block'
          },
          titleField: {
            text: 'Title',
            placeholder: 'Enter task name'
          },
          blocksSection: {
            text: 'Blocks'
          },
          blocks: {
            tabs: {
              basic: 'Basic',
              active: 'Active',
              training: 'Training',
              trainingExclusiveness:
                'Within a single task, you can only add training blocks of one type',
              games: 'Games',
              measurements: 'Measurements'
            },
            actions: {
              clone: 'Clone',
              delete: 'Delete'
            },
            groupEditor: {
              title: 'Series',
              types: {
                REPETITIONS: 'Repetitions'
              }
            },
            staticEditor: {
              name: 'Basic',
              title: 'Basic'
            },
            staticAttachmentsEditor: {
              attachmentTextPlaceholder: 'Enter comment',
              addAttachment: 'Add another attachment',
              unnamedFile: 'Unnamed file'
            },
            staticAttachmentsSlide: {
              downloadFile: 'Download file'
            },
            inputFileUploadEditor: {
              name: 'File upload',
              title: 'File upload block',
              fileType: 'File type',
              fileInstructions: 'Instructions for this file...',
              noInstructions: 'No instructions',
              addFileUpload: 'Add another attachment'
            },
            inputFileUploadSlide: {
              uploadPlaceholder: 'Choose file...'
            },
            inputTimerEditor: {
              name: 'Timer',
              title: 'Timer',
              setTime: 'Set time:'
            },
            inputBooleanEditor: {
              name: 'Yes or No',
              title: 'Yes or No',
              choices: {
                true: 'Yes',
                false: 'No'
              }
            },
            inputTextEditor: {
              name: 'Text',
              title: 'Text'
            },
            inputNumberEditor: {
              name: 'Number',
              typeTitle: 'Type of number',
              typeInteger: 'Integer',
              typeFloat: 'Number with decimal places'
            },
            inputDateTimeEditor: {
              name: 'Date / Time',
              typeTitle: 'Type',
              typeDate: 'Date',
              typeTime: 'Time',
              typeDateTime: 'Date and time'
            },
            inputSingleChoiceEditor: {
              name: 'Single choice',
              title: 'Single choice',
              addOption: 'Add option',
              maxOptionCountsReached: 'maximum number of options {{maxCount}} reached'
            },
            inputMultipleChoiceEditor: {
              name: 'Multiple choice',
              title: 'Multiple choice',
              addOption: 'Add option',
              maxOptionCountsReached: 'maximum number of options {{maxCount}} reached'
            },
            inputRatingEditor: {
              name: 'Rating',
              title: 'Rating',
              chooseMaxRating: 'Choose maximum rating',
              maxValue: 'Max value',
              minValueText: 'Where 1 means',
              minValueTextPlaceholder: 'Enter explanation',
              maxValueText: 'Where {{max}} means',
              maxValueTextPlaceholder: 'Enter explanation'
            },
            inputRangeEditor: {
              name: 'Range',
              title: 'Range'
            },
            inputBricksEditor: {
              notAvailableOnMobile: 'Bricks editor not available on mobile',
              useDesktopVersion: 'Please use the desktop version to edit bricks',
              addCategory: 'add category',
              addElement: 'add element',
              category: 'Category',
              categoryPlaceholder: 'Category name',
              hasElements: 'has {{count}} elements',
              deleteBrickConfirmationHeader: 'Are you sure you want to delete this brick?',
              deleteBrickConfirmationText:
                'This action is irreversible. After deleting the brick, it will no longer be available in this category.',
              deleteCategoryConfirmationHeader: 'Are you sure you want to delete this category?',
              deleteCategoryConfirmationText:
                'This action is irreversible. After deleting the category, it will no longer be available in the task.',
              bicksContentItemTextPlaceholder:
                'Enter instructions for the game (e.g. "Assign bricks to categories")',
              editGame: 'Edit Game'
            },
            gameEditorHeader: {
              done: 'Done'
            },
            inputTrainingEditor: {
              typeYoga: 'Yoga',
              typePilates: 'Pilates',
              typeCardio: 'Cardio',
              typeStrength: 'Strength',
              typeLapSwimming: 'Swimming',
              typeCycling: 'Cycling',
              typeRunning: 'Running',
              intensity: {
                title: 'Intensity',
                typeWarmup: 'Warmup',
                typeNormal: {
                  INPUT_TRAINING_RUNNING: 'Run',
                  INPUT_TRAINING_CYCLING: 'Bike',
                  INPUT_TRAINING_LAP_SWIMMING: 'Swim',
                  INPUT_TRAINING_STRENGTH: 'Workout',
                  INPUT_TRAINING_CARDIO: 'Workout',
                  INPUT_TRAINING_YOGA: 'Practice',
                  INPUT_TRAINING_PILATES: 'Workout'
                },
                typeCooldown: 'Cool down'
              },
              duration: {
                title: 'When to finish?',
                titleReadonly: 'Finish',
                unit: 'Unit',
                types: {
                  DURATION_CALORIES: 'After burning',
                  DURATION_DISTANCE: 'After covering',
                  DURATION_HEART_RATE: 'After reaching',
                  DURATION_OPEN: 'After clicking Lap button',
                  DURATION_POWER: 'After reaching',
                  DURATION_REPS: 'After doing',
                  DURATION_SWIM_DISTANCE: 'After covering',
                  DURATION_TIME: 'After time'
                }
              },
              target: {
                title: 'What is the goal?',
                titleReadonly: 'Goal',
                types: {
                  none: 'None',
                  TARGET_HEART_RATE: 'Heart rate zone',
                  TARGET_PACE: 'Pace',
                  TARGET_POWER: 'Power zone',
                  TARGET_RUNNING_CADENCE: 'Running cadence',
                  TARGET_SPEED: 'Speed'
                }
              }
            },
            gamePuzzleEditor: {
              name: 'Puzzle',
              title: 'Puzzle'
            },
            gameBricksEditor: {
              name: 'Bricks',
              title: 'Bricks'
            },
            gameFlashcardsEditor: {
              name: 'Flashcards game',
              title: 'Flashcards',
              flashcardsContentItemTextPlaceholder:
                'Enter instructions for the game (e.g. "Answer the questions on the flashcards")',
              assertionType: {
                title: 'Assertion type',
                text: 'Text',
                integer: 'Integer',
                decimal: 'Decimal',
                date: 'Date',
                datetime: 'Date and time',
                boolean: 'True/False',
                ai: 'AI evaluator'
              }
            },
            gameConnectorEditor: {
              name: 'Connect elements',
              title: 'Connect elements'
            },
            gameBlanksEditor: {
              name: 'Fill blanks',
              title: 'Fill Blanks Game',
              addText: 'add text',
              addBlank: 'add blank',
              editGame: 'Edit Game',
              textInputPlaceholder: 'Check if you can guess all the hidden words',
              blankWordPlaceholder: 'Blank word',
              blankExplanationPlaceholder: 'Blank Explanation',
              multipleOccurrencesMessage: 'This blank appears multiple times in the text.',
              noMatchFoundMessage: 'No match found in the text!',
              blankRemovedMessage: 'Blank has been removed from the text!',
              deleteProblemConfirmationHeader: 'Are you sure you want to delete this problem?',
              deleteProblemConfirmationText:
                'This action is irreversible. After deleting the problem, it will no longer be available in the task.',
              deleteBlankConfirmationHeader: 'Are you sure you want to delete this blank?',
              deleteBlankConfirmationText:
                'This action is irreversible. After deleting the blank, it will no longer be available in the problem.',
              problemTextPlaceholder: 'Enter your text',
              reloadingGeneratedContent: 'New content detected. Reloading…'
            },
            measurementsBloodResultEditor: {
              name: 'Blood result',
              title: 'Blood result'
            },
            measurementsGrowthChart: {
              name: 'Growth chart',
              title: 'Growth chart'
            }
          },
          validator: {
            title: 'Validator & Optimizer',
            subtitle: {
              hasErrors:
                "Unfortunately, we've detected some errors that need to be addressed before publishing.",
              clean: 'Great! No errors found!',
              cleanWithSuggestions:
                'No blocking errors — but there are suggestions worth reviewing.'
            },
            passedRulesCount_one: '{{count}} rule passed! Great!',
            passedRulesCount_other: '{{count}} rules passed! Great!',
            sectionErrorsBlanks_one: '{{count}} error! In blanks game: "{{taskTitle}}"',
            sectionErrorsBlanks_other: '{{count}} errors! In blanks game: "{{taskTitle}}"',
            sectionErrorsBricks_one: '{{count}} error! In bricks game: "{{taskTitle}}"',
            sectionErrorsBricks_other: '{{count}} errors! In bricks game: "{{taskTitle}}"',
            sectionErrorsInputText_one: '{{count}} error! In open question: "{{taskTitle}}"',
            sectionErrorsInputText_other: '{{count}} errors! In open question: "{{taskTitle}}"',
            sectionErrorsFlashcards_one: '{{count}} error! In flashcards: "{{taskTitle}}"',
            sectionErrorsFlashcards_other: '{{count}} errors! In flashcards: "{{taskTitle}}"',
            sectionWarningsTitle_one: '{{count}} warning!',
            sectionWarningsTitle_other: '{{count}} warnings!',
            sectionTipsTitle_one: '{{count}} improvement idea',
            sectionTipsTitle_other: '{{count}} improvement ideas',
            strategyTaskHeader_one: '{{count}} error in task: "{{taskTitle}}"',
            strategyTaskHeader_other: '{{count}} errors in task: "{{taskTitle}}"',
            rules: {
              blockNoProblems: 'Block has no problems',
              problemEmpty: 'Problem has no text',
              problemNoBlanks: 'Problem has no blanks',
              blankEmptyWord: 'Blank has no word',
              blankNoOccurrence: 'Blank "{{word}}" does not appear in problem text',
              blankDuplicateWord:
                'Another blank in this problem uses the same word — grading will be ambiguous',
              blockAssertionMissing: 'Game has no correct answers defined yet',
              blankMultipleOccurrences:
                'This blank has more than one active occurrence in the text',
              blankNoExplanation: 'Add an explanation to help learners',
              blockNoCategories: 'Block has no categories',
              blockNoBricks: 'Block has no bricks',
              categoryEmptyName: 'Category has no name',
              brickEmpty: 'Brick is empty — add text or media',
              categoryNoBricks: 'Category has no bricks assigned',
              brickNotAssigned: 'Brick is not assigned to any category',
              assocOrphanReference:
                'Correct answers reference a category or brick that no longer exists',
              brickInMultipleCategories: 'Brick is assigned to more than one category',
              duplicateBrickText: 'Another brick in the same category uses the same text',
              duplicateBrickTextOtherCategory:
                'Another brick in a different category uses the same text',
              identicalBrickOtherCategory:
                'An identical brick is in another category — the task is unsolvable',
              blockSingleCategory: 'Add another category so sorting makes sense',
              categorySingleBrick: 'This category has only one brick — consider adding more',
              criteriaEmptyList: 'Add at least one evaluation criterion',
              criterionEmptyTitle: 'This criterion is missing a title',
              criterionEmptyDescription: 'This criterion is missing a description'
            }
          }
        },
        goals: {
          loader: 'Loading goals',
          filters: {
            all: 'All goals',
            drafts: 'Drafts',
            published: 'Published',
            favourites: 'Favourites'
          },
          filtersTitle: 'Choose goals to explore',
          user: {
            placeholder: 'Select a task from the list',
            mentorApproved: {
              approve: 'Approve',
              success: 'Feedback approved and sent to the student',
              error: 'Failed to send feedback. Please try again.',
              refreshError:
                'Feedback approved, but failed to refresh the view. Please reload the page.'
            },
            feedbackRequirementType: {
              REQUIRED: 'REQUIRED',
              OPTIONAL: 'OPTIONAL'
            }
          }
        },
        tasks: {
          loader: 'Loading tasks',
          orDivider: 'or',
          returnToInbox: 'Return to Inbox',
          closeAndExit: 'Close',
          filters: {
            all: 'All tasks'
          },
          create: 'New task',
          filtersTitle: 'Choose tasks to explore',
          endSlide: {
            label: {
              completed: 'TASK COMPLETED',
              almostCompleted: 'TASK "ALMOST" COMPLETED',
              inProgress: 'TASK IN PROGRESS',
              needsConsolidation: 'TASK NEEDS CONSOLIDATION',
              notStarted: 'TASK NOT STARTED'
            },
            heading: {
              excellent: 'Excellent! You have mastered all the material!',
              someSkipped: 'Pretty good progress! But some blocks were skipped ;-)',
              consolidation: 'Tougher blocks are worth revisiting — pick one to retry.',
              notStarted: 'Time to start! Go through the blocks and build your skills.'
            },
            stats: {
              score: 'SCORE',
              blocks: 'BLOCKS',
              time: 'TIME'
            },
            legend: {
              high: 'you know this',
              medium: "we're almost sure you know it",
              low: 'we still need to confirm',
              none: 'skipped'
            },
            clickToGoToBlock: 'CLICK TO GO TO BLOCK',
            nextStep: 'next step',
            nextStepHeading: {
              consolidation: 'In the next task, our goal is to consolidate the material!',
              newChallenge: 'Time for a new challenge! Discover the next topic.',
              keepGoing: 'Keep going! The next task will help you master the material.',
              pickUpWhereLeftOff: "You've made real progress - time to wrap it up!",
              mastered:
                "You've already mastered this topic! Pick another task from the list or revise your knowledge."
            }
          }
        },
        notifications: {
          title: 'Notifications'
        },
        verifyEmail: {
          header: 'Verify your email address',
          message: 'To log in please verify your email address. Click the link we sent you.',
          spamNote: "If you don't see the email, check your spam folder",
          backToLogin: 'Back to login page'
        }
      },
      pushNotifications: {
        title: "Check what's new",
        textStarter: 'You have',
        textJoiner: 'and',
        newMessages: 'New messages ({{count}})',
        newTasks: 'New tasks ({{count}})',
        generic: 'Open and have a look at the stream'
      },
      common: {
        tryAgain: 'Try again',
        minutes: '{{minutes}} min'
      },
      backgroundActivity: {
        upload: {
          default: 'Uploading file',
          video: 'Uploading video',
          audio: 'Uploading audio',
          image: 'Uploading image'
        },
        transcript: 'Preparing transcript…',
        default: 'Working in background…',
        toast: {
          uploadSuccess: 'File uploaded'
        }
      }
    }
  },
  pl: {
    translation: {
      shared: {
        hoursShort: 'h',
        minutesShort: 'm',
        hoursLong: 'godziny',
        minutesLong: 'minuty',
        secondsShort: 's',
        secondsLong: 'sekundy'
      },
      legacyViews: {
        redirectToNewAssignments: {
          title: 'Nowy widok dostępny',
          message:
            'Wprowadziliśmy nowy i ulepszony widok zadań. Zostaniesz automatycznie przekierowany/a za {{countdown}}',
          goToNewViewButton: 'Przejdź do nowego widoku teraz'
        }
      },
      domains: {
        legal: {
          backToHome: 'Powrót do strony',
          titles: {
            terms: 'Regulamin',
            privacy: 'Polityka prywatności',
            dpo: 'Inspektor Ochrony Danych',
            returns: 'Polityka zwrotów i odstąpienia od umowy',
            contact: 'Kontakt'
          },
          footer: {
            securePayments: 'Bezpieczne płatności:',
            terms: 'Regulamin',
            privacy: 'Polityka prywatności',
            dpo: 'Inspektor Ochrony Danych',
            returns: 'Polityka zwrotów i odstąpienia od umowy',
            contact: 'Kontakt'
          }
        },
        auth: {
          approvals: {
            title: 'Potwierdź nasze warunki i zgody',
            message:
              'Aby zapewnić Ci pełen dostęp do wszystkich funkcji i jednocześnie zadbać o przejrzystość i bezpieczeństwo Twoich danych, prosimy Cię o zapoznanie się i zaakceptowanie poniższych dokumentów oraz udzielenie zgód.',
            selectAll: 'Zaznacz wszystko',
            privacyPolicy: 'Politykę Prywatności',
            termsOfService: 'Regulamin',
            submit: 'Zatwierdź zgody',
            iHaveReadAndAccepted: 'Zapoznałem/am się i akceptuję'
          },
          sessionLost: {
            title: 'Twoja sesja wygasła',
            message:
              'Byłeś tu zalogowany wcześniej, ale Twoja sesja nie jest już ważna. Zaloguj się ponownie, aby kontynuować.',
            signInAgain: 'Zaloguj się ponownie'
          }
        },
        imprvCoins: {
          coinChip: {
            info: 'Każda odpowiedź AI ma koszt wyrażony w Imprv Coinach – tyle zużyła ta odpowiedź'
          }
        },
        mediaitems: {
          accessError: {
            blockingNetwork: {
              header: 'Prawdopodobnie Twoja sieć blokuje treść',
              text: 'Sprawdź swoje ustawienia sieci lub skontaktuj się z administratorem'
            },
            aborted: {
              header: 'Odtwarzanie media zostało przerwane',
              text: 'Spróbuj ponownie odtworzyć treść'
            },
            network: {
              header: 'Problem z siecią',
              text: 'Sprawdź swoje ustawienia sieci lub skontaktuj się z administratorem'
            },
            decode: {
              header: 'Nie udało się odtworzyć treści',
              text: 'Treść może być uszkodzona lub w nieprawidłowym formacie'
            }
          }
        },
        marketing: {
          createGoalCategorySubscription: 'Poinformuj mnie',
          creatingGoalCategorySubscription: 'Przetwarzanie ...',
          emailPlaceholder: 'Wpisz swój email',
          invalidEmailError: 'Wprowadź poprawny adres email',
          consentRequired: 'Musisz zaakceptować zgodę marketingową, aby otrzymać powiadomienie',
          alreadySubscribed:
            'Jesteś zapisany/a, będziemy Cię informować, gdy pojawią się pierwsze kursy w tej kategorii'
        },
        goals: {
          categories: {
            family: {
              label: 'rodzina',
              introText: 'Buduj środowisko, w którym rośnie potencjał.',
              ctaText:
                'Rodzina to żywy organizm, który potrzebuje uwagi, strategii i ogromnej dawki autentyczności. W imprv.ai traktujemy rodzicielstwo jako pole do ciągłego rozwoju i doskonalenia. Zaczynamy od opieki mentorskiej nad początkami życia, ale nasza wizja sięga dalej – chcemy tworzyć przestrzeń dla świadomych rodziców, którzy chcą łączyć ambicje zawodowe z budowaniem trwałych, głębokich więzi. Zapisz się na listę, by zyskać dostęp do wiedzy, która łączy naukę o rozwoju człowieka z praktyką codziennej bliskości.'
            },
            ai: {
              label: 'sztuczna inteligencja',
              introText: 'Odzyskaj czas, wzmocnij potencjał.',
              ctaText:
                'Nie uczymy AI, żebyś mógł robić mniej. Uczymy AI, żebyś mógł sięgać po więcej. W imprv.ai traktujemy algorytmy jak przedłużenie Twoich możliwości – narzędzie, które przejmuje rutynę, byś Ty mógł skupić się na kreacji, strategii i tym, co w Tobie unikalne. Zapisz się, by dowiedzieć się jako pierwszy, kiedy otworzymy dostęp do metod, które z Twojej ambicji zrobią nową rzeczywistość.'
            },
            programming: {
              label: 'programowanie',
              introText: 'Buduj świat na własnych zasadach.',
              ctaText:
                'Kod to nie tylko linijki tekstu – to fundament Twojej sprawczości. W świecie, gdzie technologia dyktuje warunki, my uczymy Cię, jak przejąć stery. Programowanie w imprv.ai to nauka precyzyjnego myślenia i przekuwania chaosu w działające rozwiązania. Dowiedz się pierwszy, kiedy ruszymy z kursami, które zmienią Cię z konsumenta technologii w jej architekta.'
            },
            dataAnalysis: {
              label: 'analiza danych',
              introText: 'Naucz się czytać między wierszami.',
              ctaText:
                'Żyjemy w szumie informacyjnym, który odbiera nam poczucie sprawczości. W imprv.ai uczymy, jak zamienić chaos danych w klarowne wnioski i skuteczne decyzje. To nie jest kurs z obsługi arkuszy – to nauka obiektywnego patrzenia na świat i Twoje projekty. Dowiedz się pierwszy, kiedy otworzymy dostęp do narzędzi, które pozwolą Ci widzieć wzorce tam, gdzie inni widzą tylko przypadek.'
            },
            science: {
              label: 'nauka',
              introText: 'Poznaj reguły gry.',
              ctaText:
                'Matematyka, fizyka i logika to nie tylko podręcznikowe wzory – to system operacyjny świata. Wierzymy, że zrozumienie fundamentów daje przewagę, której nie zastąpi żadna intuicja. Uczymy nauk ścisłych, abyś przestał zgadywać, a zaczął przewidywać i projektować z matematyczną precyzją. Zapisz się, by dowiedzieć się, kiedy otworzymy kursy, które zamienią suchą teorię w Twoją najpotężniejszą broń.'
            },
            foreignLanguages: {
              label: 'języki obce',
              introText: 'Zainstaluj nową perspektywę.',
              ctaText:
                'Język to nie tylko gramatyka – to system operacyjny, który pozwala Ci działać na globalnym rynku. W imprv.ai uczymy języków jako narzędzi do zdobywania wiedzy i budowania relacji bez barier. Wykorzystujemy AI, by radykalnie skrócić czas nauki i skupić się na tym, co najważniejsze: swobodzie wyrażania Twojego potencjału. Dowiedz się pierwszy, kiedy otworzymy dostęp do metod, które pozwolą Ci myśleć i tworzyć w nowym języku.'
            },
            business: {
              label: 'biznes',
              introText: 'Buduj organizmy, nie tylko organizacje.',
              ctaText:
                'W imprv.ai wierzymy, że biznes to coś więcej niż cyfry w Excelu – to żywy ekosystem napędzany Twoją wizją i dyscypliną. Uczymy holistycznego podejścia, w którym strategia łączy się z psychologią, a nowoczesne narzędzia AI wspierają ludzki potencjał. Dowiedz się pierwszy o kursach, które nauczą Cię projektować przedsięwzięcia odporne na chaos, zgodne z Twoimi wartościami i gotowe na nowe wyzwania.'
            },
            longevity: {
              label: 'długowieczność',
              introText: 'Zaprojektuj swoją przyszłość.',
              ctaText:
                'Wierzymy, że prawdziwy rozwój jest niemożliwy bez dbania o fundament, jakim jest organizm. Nie możemy pozwolić, by czas odebrał nam sprawczość. Nasze holistyczne podejście łączy dane, biotechnologię i codzienną dyscyplinę, by dać Ci przewagę, której nie kupisz za żadne pieniądze – czas w pełni sił. To nauka o tym, jak być "kimś więcej" nie tylko dziś, ale i za trzydzieści lat. Bądź pierwszy, gdy udostępnimy wiedzę, która przywraca wiarę w nieograniczone możliwości ludzkiego ciała.'
            },
            sportAndFitness: {
              label: 'sport i fitness',
              introText: 'Zbuduj fundament pod swój potencjał.',
              ctaText:
                'Wierzymy, że wysoki poziom ambicji wymaga sprawnego „silnika”. Nasze podejście do fitnessu jest holistyczne: łączymy naukę o ruchu, regenerację i technologię, by Twój organizm nadążył za Twoim umysłem. To nie są zwykłe treningi – to optymalizacja Twojej mobilności i energii, byś nigdy nie poczuł, że ciało ogranicza Twoje plany. Dołącz do listy oczekujących i zacznij trenować z głową.'
            },
            genius: {
              label: 'geniusz',
              introText: 'Stań się kimś więcej.',
              ctaText:
                'Współczesny świat tłumi Twój potencjał rozpraszaczami. My przywracamy Ci sprawczość. Kategoria Geniusz to esencja imprv.ai – trening dla tych, którzy chcą widzieć więcej, rozumieć szybciej i budować rzeczy, które wydają się niemożliwe. To nie są zwykłe kursy, to proces stawania się najlepszą wersją siebie. Bądź pierwszy, gdy odkryjemy karty.'
            },
            art: {
              label: 'sztuka',
              introText: 'To, czego nie podrobi algorytm.',
              ctaText:
                'W świecie zdominowanym przez technologię, Twoja kreatywność jest najcenniejszą walutą. W imprv.ai traktujemy sztukę jako trening nieszablonowego myślenia i odwagę do wyrażania własnej wizji. Uczymy, jak przekuć wrażliwość w siłę projektową, która wyróżni Cię w każdej dziedzinie życia. Dowiedz się pierwszy o kursach, które nauczą Cię tworzyć rzeczy unikalne, ważne i głęboko ludzkie.'
            },
            skills: {
              label: 'umiejętności',
              introText: 'Twoje unikalne API do świata.',
              ctaText:
                'Współczesny świat uczy nas bycia konsumentami, my uczymy bycia twórcami i operatorami. Ta kategoria to hołd dla zaradności i głodu potrafienia rzeczy trudnych, pięknych i użytecznych. Odzyskaj sprawczość poprzez opanowanie konkretnych narzędzi i metod, które pozwolą Ci działać poza schematami. Bądź pierwszy, gdy ruszymy z różnorodnymi kursami, które udowodnią, że Twój potencjał nie ma granic.'
            }
          },
          materialsAvailableFrom: 'Materiały dostępne od',
          availableAfter: 'Dostępny od {{date}}',
          mapButtonText: 'mapa',
          comboGraphTitle: 'Mapa: {{title}}',
          couldNotReadGoals: 'Nie udało się odczytać celów',
          contentListTitle: 'Zawartość',
          contentListShowMore: 'pokaż wszystko',
          contentListShowLess: 'schowaj',
          optionalNodeLabel: 'nie wlicza się do wyniku końcowego',
          optionalGoalLabel: 'Postęp nie wlicza się do wyniku końcowego',
          comboGoalsHorizontalListHeader: 'Ten cel jest również zawarty w:',
          newContent: {
            marker: 'Nowe',
            notice: 'Nowe materiały w kursie — kliknij, aby przejść'
          },
          sellingStatus: {
            preSale: 'PRZEDSPRZEDAŻ',
            soldOut: 'WYPRZEDANE',
            new: 'NOWOŚĆ',
            startsOn: 'startujemy {{date}}'
          },
          landingPage: {
            previewVideo: {
              playButtonLabel: 'Odtwórz film',
              iframeTitle: 'Film poglądowy'
            },
            preSaleBanner: {
              title: 'PRZEDSPRZEDAŻ',
              subtitle: 'Skorzystaj z oferty i zapisz się przedpremierowo na kurs w niższej cenie!'
            },
            soldOutBanner: {
              title: 'WYPRZEDANE',
              subtitle:
                'Ten kurs jest wyprzedany. Sprawdź inne kursy, które mogą Cię zainteresować!'
            },
            menu: {
              aboutMentor: 'Mentor',
              tableOfContents: 'Spis Treści',
              courses: 'Kursy',
              pricing: 'Cennik',
              faq: 'FAQ',
              discord: 'Dołącz do nas na Discord',
              share: 'Udostępnij'
            },
            discordBanner: {
              text: 'Dołącz do społeczności <strong>ponad 4 000 osób</strong> — i bądź pierwszy przy zniżkach, darmowych materiałach i wsparciu mentorów.',
              cta: 'Dołącz za darmo'
            },
            recommendations: {
              title: 'Rekomendowane',
              subtitle: 'Poniżej znajdziesz zestaw kursów, które mogą Cię zainteresować.'
            },
            mentor: {
              female: {
                title: 'Mentorka',
                leader: 'prowadząca',
                introduction: 'Będziesz realizować cel pod opieką znakomitej specjalistki'
              },
              male: {
                title: 'Mentor',
                leader: 'prowadzący',
                introduction: 'Będziesz realizować cel pod opieką znakomitego specjalisty'
              },
              many: {
                title: 'Mentorzy',
                leader: 'prowadzący',
                introduction: 'Będziesz realizować cel pod opieką znakomitych specjalistów'
              },
              non_binary: {
                title: 'Mentor',
                leader: 'prowadzący',
                introduction: 'Będziesz realizować cel pod opieką znakomitej osoby'
              },
              dont_want_to_disclose: {
                title: 'Mentor',
                leader: 'prowadzący',
                introduction: 'Będziesz realizować cel pod opieką znakomitej osoby'
              }
            },
            faq: {
              haveQuestions: 'Może masz pytania?',
              checkQuestions:
                'Rzuć okiem na listę popularnych pytań. Może tutaj znajdziesz odpowiedź. Jeśli nie, napisz na: ={{email}}=',
              sendEmail: 'Wyślij maila'
            },
            toc: {
              title: 'Poznaj szczegółowy =plan szkolenia=',
              sectionTitle: 'Poznaj szczegółowy plan szkolenia',
              sectionLeader: 'plan szkolenia',
              sectionTitlePlaceholder: 'Poznaj szczegółowy plan szkolenia',
              module: 'Moduł {{number}}'
            },
            keywords: {
              sectionTitle: 'Czego =się nauczysz='
            },
            pricing: {
              benefitsTitle: 'Dzięki kursowi imprv® zyskujesz:',
              whatIncludedTitle: 'Co otrzymujesz?',
              forFree: 'Darmowy kurs',
              onlineCourse: 'Szkolenie online',
              preSaleCTA:
                'Skorzystaj z oferty i zapisz się przedpremierowo na kurs w niższej cenie!',
              startDate: 'Kurs startuje w dniu',
              course: '/kurs',
              youSave: 'oszczędzasz {{amount}} PLN ({{percentage}}%)',
              alreadyOwned: 'Kurs już posiadasz'
            },
            editor: {
              hero: {
                titlePlaceholder: 'Tytuł celu',
                shortDescriptionPlaceholder: 'Krótki opis',
                ctaLabelPlaceholder: 'Tekst przycisku',
                ctaSecondaryLabelPlaceholder: 'Tekst przycisku',
                uploadImagePlaceholder: 'Zmień obraz hero',
                imagePlaceholder: 'Zdjęcie'
              },
              overview: {
                titlePlaceholder: 'Tytuł',
                subtitlePlaceholder: 'Podtytuł',
                descriptionPlaceholder: 'Opis',
                benefitTitlePlaceholder: 'Tytuł korzyści',
                benefitItemPlaceholder: 'Korzyść',
                addItem: 'wiersz',
                removeItem: 'Usuń element'
              },
              faq: {
                questionPlaceholder: 'Pytanie',
                answerPlaceholder: 'Odpowiedź',
                addFaq: 'pytanie',
                removeFaq: 'Usuń FAQ'
              },
              mentorBio: {
                sectionTitlePlaceholder: 'Nagłówek sekcji (kolor)',
                introductionPlaceholder: 'Wprowadzenie do sekcji',
                namePlaceholder: 'Imię mentora',
                titlePlaceholder: 'Stanowisko / rola',
                bioPlaceholder: 'Bio',
                addMentor: 'mentor',
                removeMentor: 'Usuń mentora'
              },
              modules: {
                titlePlaceholder: 'Tytuł modułu',
                descriptionPlaceholder: 'Opis modułu',
                addModule: 'moduł',
                removeModule: 'Usuń moduł'
              },
              pricing: {
                titlePlaceholder: 'Tytuł sekcji',
                descriptionPlaceholder: 'Opis',
                benefitPlaceholder: 'Korzyść',
                includedTitlePlaceholder: 'Tytuł zawartości',
                includedItemPlaceholder: 'Element zawartości',
                addBenefit: 'zysk',
                removeBenefit: 'zysk',
                addIncluded: 'benefit',
                removeIncluded: 'benefit'
              },
              socialProof: {
                titlePlaceholder: 'Tytuł sekcji',
                subtitlePlaceholder: 'Podtytuł',
                statisticLabelPlaceholder: 'Etykieta',
                featurePlaceholder: 'Tekst cechy',
                addStatistic: 'statystyka',
                removeStatistic: 'Usuń statystykę',
                addFeature: 'cecha',
                removeFeature: 'Usuń cechę',
                iconPicker: {
                  trigger: 'Zmień ikonę',
                  icon: {
                    check: 'Ptaszek',
                    star: 'Gwiazdka',
                    heart: 'Serce',
                    person: 'Osoba',
                    cup: 'Puchar',
                    rocket: 'Rakieta',
                    sparkles: 'Iskry',
                    trending: 'Wzrost'
                  }
                }
              },
              testimonials: {
                quotePlaceholder: 'Cytat',
                authorPlaceholder: 'Imię autora',
                rolePlaceholder: 'Rola autora',
                addTestimonial: 'opinia',
                removeTestimonial: 'Usuń opinię'
              },
              previewVideo: {
                title: 'Wideo podglądowe',
                urlPlaceholder: 'Wklej link z YouTube lub Vimeo',
                titlePlaceholder: 'Tytuł wideo (opcjonalnie)',
                subtitlePlaceholder: 'Podtytuł wideo (opcjonalnie)',
                invalidUrl: 'Podaj prawidłowy link z YouTube lub Vimeo'
              },
              keywords: {
                title: 'Słowa kluczowe',
                sectionTitlePlaceholder: 'Czego się nauczysz',
                keywordPlaceholder: 'Dodaj słowo kluczowe',
                keywordInputLabel: 'Słowo kluczowe',
                addKeyword: 'Dodaj słowo kluczowe',
                removeKeyword: 'Usuń słowo kluczowe'
              },
              visibility: {
                show: 'Pokaż sekcję na stronie',
                hide: 'Ukryj sekcję na stronie',
                hiddenBadge: 'Ukryta'
              },
              exitPrompt: {
                title: 'Niezapisane zmiany',
                message: 'Masz niezapisane zmiany. Zapisać je przed wyjściem?',
                save: 'Zapisz',
                dismiss: 'Nie teraz'
              },
              genaiSwitch: {
                label: 'Generuj z AI'
              }
            }
          }
        },
        goalPackages: {
          landingPage: {
            pay: 'Zapłać',
            insteadOf: 'zamiast',
            youSavePercentage: 'oszczędzasz {{amount}} PLN ({{percentage}}%)',
            buyNow: 'Kup pakiet teraz',
            buyPayU: 'Kup z PayU',
            buyStripe: 'Kup przez Stripe',
            buyStripeHint: '(użyj kodu rabatowego)',
            processing: 'Przetwarzanie...',
            mentorsTitle: 'Poznaj mentorów',
            mentorsIntro:
              'Ucz się pod okiem doświadczonych specjalistów, którzy przeprowadzą Cię przez każdy krok.',
            pricingBenefitsTitle: 'Dzięki pakietowi zyskujesz:',
            pricingOnlineCourse: 'Szkolenie online',
            pricingWhatIncludedTitle: 'Co otrzymujesz?',
            perPackageSuffix: '/pakiet',
            alreadyAcquired: 'Posiadasz już wszystkie kursy z tego pakietu.',
            isFree: 'Pakiet został dodany do Twojego konta.'
          }
        },
        goalSubscriptionVerification: {
          title: 'Certyfikat ukończenia',
          startedAt: 'Data rozpoczęcia',
          completedAt: 'Data zakończenia',
          completedAtNotSetYet: 'W trakcie...',
          completedPercentage: 'Procent ukończenia',
          finalResult: 'Wynik końcowy',
          currentResult: 'Aktualny wynik',
          confirmInfix: 'potwierdza, że',
          finishInfix: {
            MALE: 'ukończył kurs',
            FEMALE: 'ukończyła kurs',
            NON_BINARY: 'ukończył/a kurs',
            DONT_WANT_TO_DISCLOSE: 'ukończył/a kurs'
          },
          mentorInfix: {
            MALE: 'prowadzący',
            FEMALE: 'prowadząca',
            NON_BINARY: 'prowadzący',
            DONT_WANT_TO_DISCLOSE: 'prowadzący'
          },
          downloadCertificate: 'Pobierz certyfikat',
          stillInProgress: {
            text: 'Kurs wciąż trwa, certyfikat nie jest jeszcze dostępny. Jeżeli chcesz zaktualizować dane używane do certyfikatu',
            linkText: 'kliknij tutaj'
          },
          buyItBanner: {
            text: 'Twoje imię na certyfikacie – to możliwe!',
            CTAText: 'Odkryj kurs i zdobądź go'
          },
          errorBanner: {
            DELETED: {
              title: 'Realizacja kursu anulowana',
              description: 'Kurs nie został zakończony, w związku z anulowaniem jego realizacji'
            },
            CANCELLED: {
              title: 'Realizacja kursu anulowana',
              description: 'Kurs nie został zakończony, w związku z anulowaniem jego realizacji'
            }
          },
          newContentBanner: {
            title: 'W kursie pojawiły się nowe materiały',
            description:
              'Twój certyfikat i wynik pozostają bez zmian — nowe materiały mogą go tylko poprawić.',
            CTAText: 'Przejdź do nowych materiałów'
          }
        },
        goalSubscriptions: {
          preSaleWaitInfo: {
            cta: 'Przejdź do strony głównej kursu',
            titlePart1: 'Kurs jest w trakcie przygotowywania i ruszy od',
            titlePart2: 'jak tylko wystartujemy zostaniesz poinformowany/a',
            tasksPlaceholder: 'Tutaj pojawią się zadania do realizacji w ramach niniejszego kursu'
          },
          chatTabTitle: 'Czat',
          goToTasks: 'Przejdź do zadań',
          settingsTabTitle: 'Ustawienia',
          infoTabTitle: 'Info',
          pending: {
            justAMoment: 'Jeszcze chwilka...',
            processingPayment: 'Pracujemy nad procesowaniem Twojej płatności',
            inProgress: 'W trakcie przetwarzania',
            redirection: 'Przekierowanie nastąpi za ',
            redirectionSeconds: '{{count}} sekund',
            paymentInProgress:
              'Płatność się nie powiodła lub wciąż jest przetwarzana, spróbuj ponownie'
          },
          cancelled: {
            ohNo: 'O nie!',
            sorryToSeeYouGo:
              'Przykro nam, że rezygnujesz z realizacji celu. Mamy jednak nadzieję, że jeszcze się spotkamy. Trzymaj się!'
          },
          cancelPayment: 'Anuluj płatność',
          externalSaleTitle: 'Cel jest sprzedawany na zewnętrznej platformie',
          externalSaleDescription: 'Przejdź do koszyka sprzedawcy',
          status: {
            pending: 'Oczekuje',
            active: 'Aktywny',
            paused: 'Wstrzymany',
            completed: 'Zakończony',
            cancelled: 'Anulowany',
            acquired: 'Zakupiony',
            accessGranted: 'Dostęp nadany'
          }
        },
        subscriptions: {
          accessDenied: {
            title: 'Cel został anulowany',
            message: 'Dostęp do treści został zablokowany'
          }
        },
        payments: {
          CTAs: {
            acquired: 'Kurs zakupiony',
            enrollReasonAcquiredAtImprv:
              'Masz dostęp do tego kursu – możesz rozpocząć naukę w każdej chwili',
            enrollReasonAcquiredAtGoToIt:
              'Kurs został zakupiony przez GoToIT, możesz rozpocząć naukę w każdej chwili',
            enrollReasonFree: 'Kurs jest darmowy, możesz rozpocząć naukę w każdej chwili',
            buyNow: 'Kup teraz',
            buyNowPayU: 'Zapłać przez PayU (BLIK / przelew)',
            paid: 'Zapisuję się na kurs',
            free: 'Zapisuję się na darmowy kurs',
            soldOut: 'Zobacz podobne kursy',
            processingFree: 'Startujemy, jeszcze chwilka...',
            processingPayment: 'Twoja płatność jest przetwarzana...',
            authorCannotEnroll:
              'Jako mentor nie możesz realizować własnych celów. Przejdź do konta improvera lub skontaktuj się z hello@imprv.ai',
            enrollNow: 'Zacznij naukę',
            loginAndEnroll: 'Zaloguj się aby zacząć naukę',
            processing: 'Przetwarzanie...',
            learnNow: 'Przejdź do kursu',
            programView: 'Zobacz program',
            login: 'Zaloguj się'
          },
          thankYou: {
            titlePart1: 'Gratulacje! Dostęp do kursu',
            titlePart2: 'został zakupiony',
            packageTitlePart1: 'Gratulacje! Dostęp do pakietu',
            packageTitlePart2: 'został zakupiony',
            notEnrollable:
              'Coś poszło nie tak, czy jesteś już zapisany/a na kurs? Pisz do nas na help@imprv.ai',
            soldOut: 'Ten kurs jest wyprzedany. Sprawdź inne kursy, które mogą Cię zainteresować!',
            backToHome: 'Wróć do strony głównej',
            processingOrder: 'Przetwarzamy Twoje zamówienie',
            imprvCoins: {
              title: 'Gratulacje!',
              credited: 'Imprv Coiny zostały dodane do Twojego konta',
              creditedNeutral:
                'Płatność zakończona – Imprv Coiny wkrótce pojawią się w Twojej historii',
              timeout:
                'Płatność trwa dłużej niż zwykle – Twoje coiny pojawią się na koncie za kilka minut. Jeśli nie dotrą, napisz do help@imprv.ai',
              back: 'Wróć'
            }
          },
          linkThankYou: {
            heading: 'Dziękujemy za płatność',
            message: 'Otrzymaliśmy Twoją płatność. Odezwiemy się do Ciebie w ciągu 24 godzin.',
            errorHeading: 'Płatność nie doszła do skutku',
            errorMessage:
              'Nie pobraliśmy żadnej opłaty. Możesz spróbować ponownie w dowolnym momencie.',
            retry: 'Spróbuj zapłacić ponownie',
            exploreCourses: 'Zobacz inne kursy'
          },
          cancel: {
            titlePart1: 'Anulowałeś/aś płatność za kurs',
            packageTitlePart1: 'Anulowałeś/aś płatność za pakiet',
            titlePart2: 'Nie pobraliśmy żadnej opłaty.',
            titlePart3: 'Zakup możesz dokończyć w dowolnym momencie.',
            goToGoal: 'Przejdź do kursu',
            goToPackage: 'Przejdź do pakietu',
            backToHome: 'Wróć do strony głównej',
            loader: 'Ładowanie...',
            imprvCoins: {
              title: 'Płatność została anulowana',
              back: 'Wróć'
            }
          }
        },
        assignments: {
          toc: {
            end: 'Koniec',
            start: 'Startujemy!'
          },
          task: {
            navigation: {
              start: 'Start',
              previous: 'Poprzedni',
              check: 'Sprawdzam',
              submitForReview: 'Prześlij do sprawdzenia',
              next: 'Dalej',
              tryAgain: 'Kolejna próba',
              buyCoins: 'Doładuj',
              coinsWarning: {
                lowBalance_one: 'Został Ci <b>{{count}} coin</b>',
                lowBalance_few: 'Zostały Ci <b>{{count}} coiny</b>',
                lowBalance_many: 'Zostało Ci <b>{{count}} coinów</b>',
                expiring_one: '<b>{{count}} coin</b> wygaśnie <b>{{date}}</b>',
                expiring_few: '<b>{{count}} coiny</b> wygasną <b>{{date}}</b>',
                expiring_many: '<b>{{count}} coinów</b> wygaśnie <b>{{date}}</b>',
                lowBalanceExpiring_one:
                  'Został Ci <b>{{count}} coin</b>, <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>',
                lowBalanceExpiring_few:
                  'Zostały Ci <b>{{count}} coiny</b>, <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>',
                lowBalanceExpiring_many:
                  'Zostało Ci <b>{{count}} coinów</b>, <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>',
                expiringVerb_one: 'wygaśnie',
                expiringVerb_few: 'wygasną',
                expiringVerb_many: 'wygaśnie',
                dismiss: 'Zamknij powiadomienie'
              }
            },
            toc: {
              end: 'Koniec',
              start: 'Początek',
              stageMap: 'MAPA ETAPÓW'
            }
          },
          missingUserInputToast:
            'Przejdź do etapów oznaczonych żółtym kolorem i wypełnij brakujące dane',
          missingUserInputText: 'Wypełnij brakujące dane zanim zakończysz zadanie'
        },
        blocks: {
          inputText: {
            label: 'Twoja odpowiedź',
            placeholder: 'Wpisz odpowiedź'
          },
          inputDatetime: {
            label: 'Twoja odpowiedź',
            day: '--',
            month: '---',
            year: '----',
            hour: '--',
            minute: '--',
            pickerHours: 'Godziny',
            pickerMinutes: 'Minuty',
            months: [
              'sty',
              'lut',
              'mar',
              'kwi',
              'maj',
              'cze',
              'lip',
              'sie',
              'wrz',
              'paź',
              'lis',
              'gru'
            ]
          },
          inputNumber: {
            label: 'Twoja odpowiedź',
            placeholder: 'Wpisz odpowiedź'
          },
          inputSingleChoice: {
            label: 'Twoja odpowiedź'
          },
          inputMultipleChoice: {
            label: 'Twoja odpowiedź'
          },
          inputBoolean: {
            label: 'Twoja odpowiedź'
          },
          inputRating: {
            label: 'Twoja odpowiedź'
          },
          inputFeedback: {
            checkingAnswer: 'Sprawdzam odpowiedź...',
            yourScore: 'Twój wynik',
            correctAnswer: 'Poprawna odpowiedź',
            correctAnswerIs: 'Poprawna odpowiedź to',
            feedback: 'Feedback',
            seeHowYouDid: 'Zobacz jak Ci poszło',
            nothingToAssessYet:
              'Tym razem nie ma czego ocenić. Następnym razem spróbuj napisać choćby jedno zdanie własnymi słowami.'
          },
          inputFileUpload: {
            label: 'Twoja odpowiedź',
            afterUploadFeedback:
              'Twoje <strong>rozwiązanie</strong> zostało <strong>przesłane do sprawdzenia</strong>, gdy tylko zostanie ocenione otrzymasz wiadomość na skrzynkę mailową.',
            afterUploadFeedbackFailed:
              'Nie udało się wygenerować feedbacku dla tego zgłoszenia z powodu błędu technicznego. Użyj przycisku <strong>Kolejna próba</strong>, aby wysłać pliki jeszcze raz.',
            addFeedback: 'dodaj feedback',
            deleteFeedback: {
              header: 'Usuń feedback',
              text: 'Czy na pewno chcesz usunąć ten wpis feedbacku?'
            }
          },
          inputBricks: {
            title: 'Gra Spadające Klocki',
            start: 'Rozpocznij grę',
            completed: 'Gra ukończona!',
            score: 'Wynik: {{score}} %',
            perfect: 'Doskonale! Wszystkie klocki zostały umieszczone poprawnie!',
            wellDone: 'Wspaniale!',
            goodJob: 'Dobra robota! Ćwicz dalej, aby poprawić swój wynik.',
            keepPracticing: 'Ćwicz dalej, aby poprawić swój wynik.',
            tryAgain: 'ZAGRAJ PONOWNIE',
            previousResults: 'Pokaż poprzednie wyniki',
            seeHowYouDid: 'Zobacz jak Ci poszło',
            answeredCorrectly: 'Odpowiedziałeś/aś poprawnie',
            youSelected: 'Wybrałeś/aś',
            noSelection: 'Brak wybranej kategorii',
            notSelectedInTime: 'Nie wybrano kategorii, sprawdź poprawną',
            correctAnswerIs: 'Poprawna odpowiedź',
            correctlySelectedCategory: 'Brawo! Poprawna kategoria wybrana',
            incorrectlySelectedCategory:
              'Niestety nie udało się wybrać dobrej kategorii, sprawdź poprawną',
            evaluationError: 'Wystąpił błąd podczas oceniania wyników. Spróbuj ponownie.'
          },
          inputBlanks: {
            title: 'Gra Uzupełnij Luki',
            start: 'Rozpocznij grę',
            completed: 'Gra ukończona!',
            score: 'Wynik: {{score}} %',
            perfect: 'Doskonale! Wszystkie luki zostały uzupełnione poprawnie!',
            wellDone: 'Wspaniale!',
            goodJob: 'Dobra robota! Ćwicz dalej, aby poprawić swój wynik.',
            keepPracticing: 'Ćwicz dalej, aby poprawić swój wynik.',
            tryAgain: 'ZAGRAJ PONOWNIE',
            attempt: 'Podejście ',
            correctAnswer: 'Poprawna odpowiedź',
            hint: 'Podpowiedź',
            previousResults: 'Pokaż poprzednie wyniki',
            seeHowYouDid: 'Zobacz jak Ci poszło'
          },
          inputFlashcards: {
            title: 'Gra Fiszki',
            completed: 'Gra ukończona!',
            seeHowYouDid: 'Zobacz jak Ci poszło',
            evaluationError: 'Wystąpił błąd podczas oceniania wyników. Spróbuj ponownie.',
            slide: {
              startTitle: 'Rozpocznij fiszki',
              start: 'Rozpocznij',
              check: 'Sprawdzam',
              next: 'Dalej',
              retry: 'Spróbuj ponownie',
              evaluationError: 'Wystąpił błąd oceny. Spróbuj ponownie.',
              tryAgain: 'Kolejna próba',
              yourAnswer: 'Tutaj wpisz lub podyktuj swoją odpowiedź',
              hardExit: {
                title: 'Czy na pewno chcesz wyjść?',
                body: 'Twój postęp w tej próbie zostanie utracony.',
                confirm: 'Wyjdź',
                cancel: 'Wróć do gry'
              },
              boolean: {
                true: 'Tak',
                false: 'Nie'
              }
            },
            editor: {
              questionPlaceholder: 'polecenie',
              questionLabel: 'PYTANIE',
              contentLabel: 'TREŚĆ',
              expectedAnswerLabel: 'OCZEKIWANA ODPOWIEDŹ',
              addCard: 'Dodaj fiszkę',
              editGame: 'Edytuj grę',
              limitAnswerTime: 'Ogranicz czas na udzielenie odpowiedzi',
              answerType: {
                text: 'Tekst',
                number: 'Liczba',
                date: 'Data',
                dateTime: 'Data i godzina',
                boolean: 'Tak/Nie'
              },
              text: {
                exactPlaceholder: 'Oczekiwana odpowiedź'
              },
              boolean: {
                true: 'Tak',
                false: 'Nie'
              }
            }
          },
          criteria: {
            sectionHeader: 'KRYTERIA OCENY',
            addCriterion: 'dodaj kryterium',
            removeAllCriteria: 'Usuń wszystkie kryteria',
            titlePlaceholder: 'Tytuł kryterium',
            descriptionPlaceholder: 'Pełny opis kryterium',
            typeLabel: {
              REQUIRED: 'WYMAGANE',
              OPTIONAL: 'OPCJONALNE'
            },
            deleteCriterion: {
              header: 'Usunąć kryterium?',
              text: 'Tej akcji nie można cofnąć.'
            },
            deleteAllCriteria: {
              header: 'Usunąć wszystkie kryteria?',
              text: 'Stracisz wszystkie zdefiniowane kryteria oceny. Tej akcji nie można cofnąć.'
            }
          },
          agentFeedback: {
            title: 'ZOBACZ JAK CI POSZŁO',
            loading: {
              text: 'AI ocenia twoją odpowiedź...',
              longText: 'Trwa to dłużej niż zwykle, AI nadal pracuje...'
            },
            error: {
              text: 'Coś poszło nie tak. Spróbuj ponownie.'
            }
          },
          attemptsHistory: {
            showPrevious: 'Pokaż poprzednie wyniki',
            hidePrevious: 'Schowaj poprzednie wyniki'
          }
        },
        tasks: {
          blocks: {
            contentItem: {
              textInputPlaceholder: 'Wpisz tekst',
              uploadButtonText: 'Załącz video, audio lub obraz'
            }
          }
        },
        schedules: {
          scheduleInProgress:
            'Planowanie w toku... Aktualizacje mogą wpłynąć na Twój plan działania. Proszę czekać.'
        },
        search: {
          noResults: 'Nie znaleziono wyników',
          moreResults: '... {{count}} więcej wyników',
          hideResults: 'ukryj wyniki'
        }
      },
      // -- LEGACY BELOW
      units: {
        bpm: {
          short: 'bpm'
        },
        celsius: {
          short: '°C'
        },
        hrZone: {
          short: 'strefa tętna'
        },
        kcal: {
          short: 'kcal'
        },
        kilometer: {
          short: 'km'
        },
        kilometerPerHour: {
          short: 'km/h'
        },
        meter: {
          short: 'm'
        },
        meterPerSecond: {
          short: 'm/s'
        },
        mile: {
          short: 'mi'
        },
        minPerKm: {
          short: 'min/km'
        },
        percent: {
          short: '%'
        },
        rpm: {
          short: 'rpm'
        },
        second: {
          short: 's'
        },
        spm: {
          short: 'spm'
        },
        watt: {
          short: 'W'
        },
        yard: {
          short: 'yd'
        },
        repetitions: {
          short: 'powtórzeń'
        },
        wattZone: {
          short: 'strefa mocy'
        },
        times: {
          single: 'raz',
          plural: 'razy'
        }
      },
      errorCodes: {
        generic: {
          header: 'Oops...',
          text: 'Coś poszło nie tak. Odśwież aplikację lub spróbuj ponownie później. Jeśli problem będzie się powtarzał, skontaktuj się z nami na help@imprv.ai'
        },
        GOAL__DELETE__HAS_SUBSCRIPTIONS: {
          header: 'Nie możesz usunąć celu, który ma aktywnych Improverów',
          text: 'Cel, który próbujesz usunąć ma aktywnych Improverów, dlatego jego usunięcie nie jest możliwe.'
        },
        ORDER__GRANT__NOT_ALLOWED: {
          header: 'Nie możesz nadać tego dostępu',
          text: 'Nie możesz nadać dostępu sobie.'
        },
        ORDER__GRANT__ALREADY_HAS_ACCESS: {
          header: 'Ta osoba ma już dostęp',
          text: 'Ta osoba ma już dostęp do tego celu.'
        },
        TASK__DELETE__USED_IN_STRATEGY: {
          header: 'Nie można usunąć zadania, które jest używane w ramach strategii',
          text: 'Zadanie, które próbujesz usunąć jest używane w ramach strategii, dlatego jego usunięcie nie jest możliwe.'
        },
        TAG__VALIDATION__TEXT_TOO_SHORT: {
          header: 'Tag jest za krótki',
          text: 'Tag musi mieć przynajmniej 1 znak'
        },
        TAG__VALIDATION__TEXT_TOO_LONG: {
          header: 'Tag jest za długi',
          text: 'Tag może mieć maksymalnie 100 znaków'
        },
        TAG__VALIDATION__TEXT_ALREADY_EXISTS: {
          header: 'Tag już istnieje',
          text: 'Tag o tej nazwie już istnieje'
        }
      },
      errors: {
        somethingWentWrong: 'Coś poszło nie tak'
      },
      activations: {
        editorTitle: 'Edytor reguły aktywacyjnej',
        tagNamePlaceholder: 'Nazwa tagu',
        tagChoiceTitle: 'Wybierz tag',
        taskNamePlaceholder: 'Nazwa zadania',
        taskChoiceTitle: 'Wybierz zadanie',
        operatorChoiceTitle: 'Wybierz operator',
        valueChoiceTitle: 'Wybierz wartość',
        ruleNamePlaceholder: 'Nazwa reguły',
        operators: {
          and: 'i',
          or: 'lub'
        },
        ruleItemsLabel: {
          path: 'Kiedy i dla kogo aktywować ścieżkę?',
          task: 'Kiedy i dla kogo aktywować zadanie?',
          message: 'Kiedy i dla kogo wyświetlić wiadomość?',
          action: 'Kiedy i dla kogo wykonać akcję?',
          group: 'Kiedy i dla kogo aktywować grupę?'
        },
        ruleNameLabel: {
          other: 'Jak nazwać tą regułę?',
          path: 'Jak nazwać tę ścieżkę?'
        },
        addPhraseLabel: 'Dodaj frazę',
        phraseTypes: {
          all: 'wszystko',
          time: 'czas',
          task: 'zadanie',
          tag: 'tag',
          weather: 'pogoda',
          group: 'grupa'
        },
        errors: {
          ruleEmpty: 'Dodaj nazwę lub regułę'
        },
        actions: {
          delete: 'Usuń'
        }
      },
      strategies: {
        publish: {
          button: 'Opublikuj',
          publishCurrentTask: 'Publikuj aktualne zadanie',
          publishTasks: 'Publikuj wszystkie zadania',
          confirmCurrentTask: {
            message:
              'Przed opublikowaniem zadania sprawdzimy jego poprawność. Jeśli wykryjemy problemy, pokażemy je w panelu walidacji, a publikacja zostanie wstrzymana.',
            confirm: 'Sprawdź i opublikuj'
          },
          confirmAllTasks: {
            message:
              'Przed opublikowaniem wszystkich zadań sprawdzimy ich poprawność. Jeśli wykryjemy problemy, pokażemy je w panelu walidacji, a publikacja zostanie wstrzymana.',
            confirm: 'Sprawdź i opublikuj'
          }
        },
        verify: {
          button: 'Weryfikuj'
        },
        common: {
          save: 'Zapisz'
        },
        editor: {
          title: 'Edytor strategii',
          emptyState: 'Dodaj sekcję aby rozpocząć'
        },
        toc: {
          sectionNamePlaceholder: 'Nazwa sekcji',
          addTaskFromLibrary: 'zadanie z listy',
          addNewTask: 'nowe zadanie',
          addSection: 'dodaj sekcję'
        },
        paths: {
          add: 'dodaj sekcję',
          startName: 'Sekcja początkowa',
          userDefinedNamePlaceholder:
            'Dodaj regułę do ścieżki żebyśmy wiedzieli kiedy i dla kogo ją aktywować',
          rulelessPathNamePlaceholder:
            'Ta ścieżka nie ma reguły więc jest niewidoczna dla użytkowników',
          emptyPathWarning:
            'Ta ścieżka nie ma żadnych elementów więc jest niewidoczna dla użytkowników',
          endName: 'Sekcja końcowa',
          actions: {
            markAsMultiActivated: 'Ścieżka może być aktywowana wielokrotnie',
            delete: 'Usuń'
          }
        },
        nodes: {
          task: {
            name: 'Zadanie',
            addTaskToPath: 'Dodaj zadanie do ścieżki',
            addTasksToPath: 'Dodaj {{count}} zadań do sekcji',
            selectedCount: '{{count}} zaznaczone',
            selectToAddToPath: 'Zaznacz zadania, które chcesz dodać do ścieżki',
            done: 'Gotowe'
          },
          actionMoveToPath: {
            title: 'Edytor akcji',
            name: 'Akcja',
            label: 'Przenieś do ścieżki',
            placeholder: 'Wybierz ścieżkę do której przenieść',
            errors: {
              pathEmpty: 'Musisz wybrać ścieżkę',
              noNamedPathsToPointTo:
                'Nie ma żadnych nazwanych ścieżek do których można przekierować'
            }
          },
          messageTemplate: {
            title: 'Edytor wiadomości',
            name: 'Wiadomość',
            label: 'Wiadomość',
            placeholder: 'Treść wiadomości',
            errors: {
              messageEmpty: 'Wiadomość nie może być pusta'
            }
          },
          actions: {
            preview: 'Podgląd',
            edit: 'Edytuj',
            markAsRequired: 'Oznacz jako wymagane',
            markAsNotRequired: 'Oznacz jako niewymagane',
            clone: 'Duplikuj',
            delete: 'Usuń'
          },
          group: {
            name: 'Grupa'
          },
          isRequired: {
            REQUIRED: 'WYMAGANE',
            OPTIONAL: 'OPCJONALNE'
          }
        }
      },
      goal: {
        defaultTitle: 'Tutaj pojawi się tytuł',
        defaultDescription: 'Tutaj pojawi się opis',
        defaultShortDescription: 'Tutaj pojawi się krótki opis',
        moreDetailsTitle: 'SZCZEGÓŁY',
        tooltipAboutParentGoal: 'Ten cel jest częścią celu COMBO'
      },
      goalPrice: {
        invalidAmountError: 'Kwota musi być większa niż 0',
        couldNotSaveError: 'Nie udało się zapisać ceny',
        creatingPrice: 'Tworzenie ceny...',
        publishing: 'Publikowanie...'
      },
      humanizedDate: {
        tomorrow: 'Jutro',
        today: 'Dzisiaj',
        yesterday: 'Wczoraj'
      },
      entities: {
        goal: 'CEL',
        task: 'ZADANIE',
        tasksLibrary: 'Biblioteka zadań',
        goalsLibrary: 'Biblioteka celów'
      },
      toasts: {
        linkCopied: 'Skopiowano link',
        somethingWentWrong: 'Coś poszło nie tak',
        markedMessageAsAlreadyReplied: 'Oznaczono jako "Odpowiedź już udzielona"',
        goalPublished: 'Cel opublikowany',
        goalPublishedStrategyFailed: 'Cel opublikowany, nie udało się opublikować strategii',
        strategyPublished: 'Strategia opublikowana',
        taskPublished: 'Zadanie opublikowane',
        goalPricePublished: 'Nowa cena została opublikowana',
        goalImageUpdated: 'Miniatura celu została zaktualizowana',
        goalImageUpdateFailed: 'Nie udało się zaktualizować miniatury celu',
        goalLandingPageUpdated: 'Landing page celu została zaktualizowana',
        goalLandingPageUpdateFailed: 'Nie udało się zaktualizować landing page celu',
        goalAlreadyEnrolled: 'Masz już dostęp do tego celu — odświeżamy stan zapisu.'
      },
      general: {
        yes: 'Tak',
        no: 'Nie',
        done: 'Gotowe',
        close: 'Zamknij',
        progress: 'Postęp',
        loading: 'Ładowanie',
        cancel: 'Anuluj',
        confirm: 'Potwierdź',
        leaveGameConfirm: 'Czy na pewno chcesz opuścić grę? Twój postęp zostanie utracony.'
      },
      components: {
        dictation: {
          start: 'Nagraj odpowiedź',
          stop: 'Zatrzymaj nagrywanie',
          microphoneBlocked:
            'Mikrofon jest zablokowany. Zezwól na dostęp w ustawieniach przeglądarki i odśwież stronę.',
          microphoneNotFound: 'Nie znaleziono mikrofonu.',
          microphoneAccessFailed: 'Brak dostępu do mikrofonu',
          transcriptionFailed: 'Transkrypcja nie powiodła się'
        },
        reorderableList: {
          reorderItem: 'Przeciągnij, aby zmienić kolejność'
        },
        transcriptDownloadButton: {
          label: 'Pobierz transkrypt',
          filename: '{{lessonTitle}} – transkrypt',
          defaultFilename: 'transkrypt'
        },
        transcriptPendingNote: {
          message:
            'Poczekaj z generowaniem kolejnych treści na przygotowanie transkryptu z załączonego pliku.'
        },
        transcriptReadyToast: {
          message: 'Transkrypt wideo jest gotowy — możesz pracować nad kolejnymi treściami.',
          openTask: 'Przejdź do zadania'
        },
        socialLinksEditor: {
          add: 'link',
          removeAriaLabel: 'Usuń link',
          choosePlatform: 'Wybierz platformę'
        },
        socialPlatforms: {
          INSTAGRAM: 'Instagram',
          FACEBOOK: 'Facebook',
          X: 'X',
          LINKEDIN: 'LinkedIn',
          YOUTUBE: 'YouTube',
          TIKTOK: 'TikTok',
          PINTEREST: 'Pinterest',
          SNAPCHAT: 'Snapchat',
          BEHANCE: 'Behance',
          GITHUB: 'GitHub',
          VIMEO: 'Vimeo',
          SOUNDCLOUD: 'SoundCloud',
          BLOG: 'Blog',
          WEBSITE: 'Strona WWW'
        },
        packagesFilterRow: {
          toggleLabel: 'pakiety'
        },
        goalUsersListPage: {
          noSubscriptions: 'Brak użytkowników',
          filters: {
            all: 'wszyscy',
            notStarted: 'nie zaczęli',
            barelyStarted: 'ledwo zaczęli',
            hardMaterial: 'trudny materiał',
            best: 'najlepsi',
            accessGranted: 'dostali dostęp'
          },
          addUser: {
            button: 'Użytkownik',
            title: 'Nadaj dostęp',
            description:
              'Podaj adres e-mail. Ta osoba dostanie dostęp jak po zakupie – także gdy cel jest prywatny. Dostęp działa po zalogowaniu na konto z tym adresem.',
            placeholder: 'Adres e-mail',
            submit: 'Dodaj',
            added: 'Dodano {{email}}',
            noNotificationNote:
              'Nie wysyłamy powiadomienia – przekaż tej osobie, że kurs jest dostępny po zalogowaniu.',
            publishFirstToast: 'Najpierw opublikuj cel',
            errors: {
              invalidEmail: 'Podaj poprawny adres e-mail.'
            }
          },
          pendingCard: {
            waiting: 'Czeka na rozpoczęcie'
          },
          grantsLoadFailedToast: 'Nie udało się wczytać nadanych dostępów'
        },
        messagesView: {
          closeConversation: 'Zamknij rozmowę',
          conversationError: 'Nie udało się wczytać rozmowy',
          doneSection: 'ZROBIONE',
          emptyState: 'Brak wiadomości',
          markDone: 'Oznacz jako zrobione',
          markPending: 'Oznacz jako niezrobione',
          pendingRecommendation: 'Rekomendacja czeka na Twoją akceptację',
          openBeforeDone: 'Zapoznaj się z rozmową, zanim oznaczysz ją jako zrobioną',
          openGoal: 'Otwórz cel',
          openGoalUser: 'Otwórz uczestnika celu',
          pendingRecommendationConfirmTitle: 'Oznaczyć jako zrobione?',
          pendingRecommendationConfirmBody:
            'Wygląda na to, że rekomendacja odpowiedzi od mentorbota wciąż czeka na Twoje potwierdzenie. Czy na pewno chcesz oznaczyć tę rozmowę jako zrobioną?',
          pendingRecommendationConfirmProceed: 'Oznacz mimo to',
          pagination: {
            range: '{{from}}–{{to}} z {{total}}',
            empty: '0 z 0',
            prev: 'Poprzednia strona',
            next: 'Następna strona'
          },
          inboxFilter: {
            trigger: 'Filtruj',
            tabs: {
              user: 'Użytkownik',
              goal: 'Cel'
            },
            searchUser: 'Szukaj użytkownika',
            searchGoal: 'Szukaj celu',
            noResults: 'Brak wyników',
            clearAll: 'Wyczyść wszystko'
          }
        },
        goalUserSelector: {
          selectUser: 'Wybierz użytkownika',
          searchPlaceholder: 'Szukaj użytkownika...',
          noResults: 'Nie znaleziono użytkowników'
        },
        tags: {
          title: 'Tagi',
          editorSearchPlaceholder: 'Szukaj lub utwórz nowy tag',
          optionRename: 'Zmień nazwę',
          optionDelete: 'Usuń wszędzie',
          deleteConfirmationHeader: 'Czy na pewno chcesz usunąć ten tag?',
          deleteConfirmationText:
            'Usunięcie tagu spowoduje usunięcie go wszędzie, gdzie jest używany'
        },
        deleteConfirmationBottomSheet: {
          confirmationButton: 'Tak',
          cancelButton: 'Nie'
        },
        imprvCoinsCounter: {
          ariaLabel: 'Saldo Imprv Coinów',
          remainingLabel: 'Zostało Ci jeszcze',
          unavailable: 'Saldo niedostępne'
        },
        goalPromoteButton: {
          publish: 'Opublikuj',
          cannotPublish: 'Nie możesz opublikować celu, ponieważ nie jest on jeszcze gotowy'
        },
        goalHubPublishButton: {
          publishAll: 'Publikuj wszystkie zmiany',
          publishGoalOnly: 'Publikuj zmiany celu',
          publishChanges: 'Publikuj zmiany',
          publishPriceFirst: 'Najpierw opublikuj cenę w zakładce Monetyzacja',
          confirmAll: {
            message:
              'Spowoduje to opublikowanie miniatury, tytułu, opisu, ceny, landing page, wszystkich sekcji oraz zadań celu. Kontynuować?'
          },
          confirmGoalOnly: {
            message:
              'Spowoduje to opublikowanie miniatury, tytułu, opisu, ceny i landing page celu. Sekcje i wszystkie zadania nie zostaną zmienione. Kontynuować?'
          },
          validationBlocked_one:
            'Cel ma {{count}} błąd walidacji. Popraw go w edytorze zadań przed publikacją.',
          validationBlocked_few:
            'Cel ma {{count}} błędy walidacji. Popraw je w edytorze zadań przed publikacją.',
          validationBlocked_many:
            'Cel ma {{count}} błędów walidacji. Popraw je w edytorze zadań przed publikacją.'
        },
        goalEditorCard: {
          editButton: 'Edytuj'
        },
        publishGoalButton: {
          publishAll: 'Publikuj wszystkie zmiany'
        },
        createEntitiesMenu: {
          create: 'Stwórz',
          createTask: 'Utwórz zadanie',
          createGoal: 'Utwórz cel',
          cannotCreateForNow:
            'Nie możesz jeszcze tworzyć zadań dla siebie. Damy Ci znać kiedy będzie to możliwe'
        },
        goalRecommendationBanner: {
          title: 'Nowe kursy już dostępne',
          cta: 'Buduj swoją przewagę'
        },
        dropdown: {
          errorMissingValue: 'Wybierz wartość'
        },
        textInput: {
          linesUsed: 'linie {{current}}/{{max}}',
          maxLengthExceeded: 'Maksymalna długość to {{maxLength}}'
        },
        numberInput: {
          errorWrongValue: 'Wpisz liczbę',
          errorWrongValueFromRange: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorNotMultiplicityOfStep: 'Wpisz liczbę będącą wielokrotnością {{step}}'
        },
        blockAssertionCorrectAnswerEditor: {
          title: 'Poprawna odpowiedź',
          chooseCorrectAnswer: 'Wybierz poprawną odpowiedź',
          provideCorrectAnswer: 'Wpisz poprawną odpowiedź',
          passMessagePlaceholder: 'Wpisz komentarz do poprawnej odpowiedzi',
          failMessagePlaceholder: 'Wpisz komentarz do błędnej odpowiedzi',
          add: 'Dodaj poprawną odpowiedź',
          addDisabledNoChoices: 'Nie można dodać poprawnej odpowiedzi gdy nie ma żadnych opcji',
          remove: 'Usuń poprawną odpowiedź',
          errorInvalidCorrectAnswer: 'wpisz poprawną odpowiedź'
        },
        blockAssertionEditor: {
          compareTypes: {
            EQUALS: 'równa się',
            GREATER_THAN: 'powyżej',
            LESS_THAN: 'poniżej',
            GREATER_THAN_OR_EQUALS: 'większa lub równa',
            LESS_THAN_OR_EQUALS: 'mniejsza lub równa',
            BETWEEN: 'z przedziału',
            UNORDERED_EQUALS: 'równa się',
            NOT_UNORDERED_EQUALS: 'nie równa się',
            CONTAINS: 'zawiera'
          },
          from: 'od',
          to: 'do',
          minGreaterThenMax: 'Wartość minimalna nie może być większa niż wartość maksymalna'
        },
        blockInputText: {
          editor: {
            evaluationMode: {
              exactAnswer: 'Wpisz poprawną odpowiedź',
              criteria: 'AI ocenia wg. kryteriów'
            }
          },
          runtime: {
            feedback: {
              retry: 'Spróbuj ponownie',
              newAttempt: 'KOLEJNA PRÓBA'
            }
          }
        },
        fileInput: {
          dragHere: 'Tutaj przeciągnij i upuść swój plik',
          upload: 'Prześlij plik',
          or: 'lub',
          progress: 'Przesłano {{progress}}%',
          errorFileSize: 'Plik jest za duży, maksymalny rozmiar to {{maxSize}}',
          errorUpload: 'Wystąpił błąd podczas przesyłania pliku',
          errorInvalidType: 'Nieprawidłowy typ pliku',
          uploadButton: 'Prześlij pliki',
          cancel: 'Anuluj'
        },
        attachmentCard: {
          defaultFileType: 'PLIK',
          unnamedFile: 'Plik bez nazwy',
          textPlaceholder: 'Dodaj opis...'
        },
        timeInput: {
          errorWrongSecondsValue: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorWrongMinutesValue: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorWrongHoursValue: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorWrongValueNoMinMax: 'Wpisz czas z przedziału od {{min}} do {{max}}',
          errorWrongValue: 'Wpisz poprawny czas'
        },
        paceInput: {
          errorWrongValueNoMinMax: 'Wpisz poprawne tempo z przedziału od {{min}} do {{max}}',
          errorWrongSecondsValue: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorWrongMinutesValue: 'Wpisz liczbę z przedziału {{min}} - {{max}}',
          errorWrongValue: 'Wpisz poprawne tempo'
        },
        goalSubscriptionStatusChangeModal: {
          onSubscribed: {
            topMessage: 'Pomyślnie zapisałeś się do nowego celu',
            bottomMessageStart:
              'Od teraz będziesz dostawać zadania i wiadomości związane z tym celem. Wszystko znajdziesz w zakładce',
            bottomMessageConversation: 'Realizowane cele',
            bottomMessageEnd: ''
          },
          onCompleted: 'Pomyślnie ukończyłeś cel!'
        },
        goalSubscriptionStrategyFinishedModal: {
          onCompleted: 'Pomyślnie ukończyłeś wszystkie zadania dla tego celu!'
        },
        markAsIrrelevant: {
          text: 'Czy chcesz oznaczyć tę wiadomość jako nie na temat?'
        },
        estimatedTimeOfReply: {
          text: 'Oczekiwany czas odpowiedzi'
        },
        replyTimeEstimation: {
          text: 'Sprawdzam kiedy możemy odpowiedzieć...'
        },
        conversations: {
          conversationInput: {
            respondingTo: 'Odpowidanie',
            placeholder: 'Napisz lub podyktuj wiadomość',
            placeholderExtraDesktop: '(wciśnij shift + enter aby dodać nową linię)',
            disabledPlaceholder: 'Czat niedostępny w trakcie gry',
            mentorbotThinkingPlaceholder: 'Mentorbot myśli...',
            messageLengthExceeded: 'Wiadomość jest za długa'
          },
          coinsNotice: {
            message: 'Nie masz już Imprv Coinów. Kup więcej, aby dalej rozmawiać z Mentorbotem.',
            buyCta: 'Kup dodatkowe Imprv Coiny',
            lowBalance_one:
              'Został Ci <b>{{count}} Imprv Coin</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            lowBalance_few:
              'Zostały Ci <b>{{count}} Imprv Coiny</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            lowBalance_many:
              'Zostało Ci <b>{{count}} Imprv Coinów</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            expiring_one:
              '<b>{{count}} Imprv Coin</b> wygaśnie <b>{{date}}</b>. Wykorzystaj go w rozmowie z Mentorbotem.',
            expiring_few:
              '<b>{{count}} Imprv Coiny</b> wygasną <b>{{date}}</b>. Wykorzystaj je w rozmowie z Mentorbotem.',
            expiring_many:
              '<b>{{count}} Imprv Coinów</b> wygaśnie <b>{{date}}</b>. Wykorzystaj je w rozmowie z Mentorbotem.',
            lowBalanceExpiring_one:
              'Został Ci <b>{{count}} Imprv Coin</b>, z czego <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            lowBalanceExpiring_few:
              'Zostały Ci <b>{{count}} Imprv Coiny</b>, z czego <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            lowBalanceExpiring_many:
              'Zostało Ci <b>{{count}} Imprv Coinów</b>, z czego <b>{{expiring}}</b> {{verb}} <b>{{date}}</b>. Doładuj, aby dalej rozmawiać z Mentorbotem.',
            expiringVerb_one: 'wygaśnie',
            expiringVerb_few: 'wygasną',
            expiringVerb_many: 'wygaśnie',
            dismiss: 'Zamknij powiadomienie'
          },
          conversationTasks: {
            noNewTasks:
              'Na razie nie mam dla Ciebie żadnych nowych zadań, ale na pewno niedługo się pojawią',
            noNewTasksSincePaused:
              'Cel jest wstrzymany, więc nie ma dla Ciebie żadnych nowych zadań'
          },
          conversationTopic: {
            whyWeAreHereSubscription: 'Zajmujemy się tutaj realizacją Twojego celu',
            whyWeAreHereGoal: 'Zajmujemy się tutaj budową i zarządzaniem celem',
            info: {
              author: 'Autor',
              subscriptionStartedDate: 'Data rozpoczęcia realizacji celu',
              subscriptionEstimatedCompleteDate: 'Przewidywana data ukończenia',
              subscriptionEstimatedCompleteDateMissing: 'Brak',
              subscriptionCost: 'Koszt realizacji celu',
              subscriptionCostFree: 'Darmowy'
            },
            participants: {
              title: 'Uczestnicy'
            },
            settings: {
              title: 'Ustawienia',
              paymentHistory: 'Historia płatności',
              pauseSubscription: 'Wstrzymaj realizację celu',
              resumeSubscription: 'Wznów realizację celu',
              goToGoalPage: 'Przejdź do strony celu',
              shareGoal: 'Udostępnij cel',
              unsubscribe: 'Zakończ realizację celu'
            }
          },
          queue: {
            tooltipQueued: 'Za chwilę wyślę...',
            tooltipSending: 'Wysyłam...',
            tooltipFailed: 'Nie udało się wysłać. Dotknij, aby spróbować ponownie.'
          },
          outcomeNotice: {
            failed: 'Coś poszło nie tak i nie udało mi się odpowiedzieć. Spróbuj ponownie.',
            recommended: 'Przygotowałem odpowiedź - czeka teraz na weryfikację Twojego mentora.',
            irrelevant: 'Nie potrafię w tym pomóc. Spróbuj sformułować pytanie inaczej.'
          },
          cancellingNotice: 'Anulowanie…'
        },
        buyImprvCoinsModal: {
          bonus: '+{{percent}}% więcej coinów',
          badgePopular: 'Najczęściej wybierany',
          starterCaption: 'Na start',
          title: 'Kup Imprv Coiny',
          subtitle:
            'Imprv Coiny wydajesz na odpowiedzi Mentorbota i inne funkcje AI. Kupione coiny nigdy nie wygasają.',
          cta: 'Kup za {{price}}',
          vatInfo: 'Cena zawiera VAT',
          error: 'Nie udało się rozpocząć płatności – spróbuj ponownie',
          packageUnavailable:
            'Ten pakiet nie jest już dostępny – odśwież aplikację i spróbuj ponownie',
          fetchError: 'Nie udało się wczytać pakietów',
          retry: 'Spróbuj ponownie'
        },
        conversationMessage: {
          inResponseTo: 'Odpowiedź',
          cancelReply: 'Zatrzymaj odpowiedź'
        },
        aiGeneratedBadge: {
          label: 'AI',
          info: 'Ta odpowiedź została wygenerowana przez sztuczną inteligencję (Imprv AI).'
        },
        conversationMessageAttachments: {
          sources: 'Źródła:'
        },
        conversationMessageResponseRecommendation: {
          recommendation: 'Rekomendacja odpowiedzi',
          counter: '{{current}} / {{total}}',
          visibilityInformation: 'Ta rekomendacja jest widoczna tylko dla Ciebie',
          sendButton: 'Wyślij',
          editButton: 'Edytuj',
          rejectButton: 'Odrzuć',
          expand: 'więcej',
          collapse: 'zwiń'
        },
        mentorbotThinking: {
          verbs: [
            'Myśli',
            'Analizuje',
            'Przygotowuje odpowiedź',
            'Rozważa',
            'Przetwarza',
            'Weryfikuje kontekst',
            'Formułuje odpowiedź',
            'Przeszukuje informacje',
            'Ocenia możliwości',
            'Układa wiadomość'
          ]
        },
        goalActions: {
          published: {
            label: 'Opublikowany'
          },
          draft: {
            label: 'Szkic'
          },
          edit: {
            label: 'Edytuj cel'
          },
          like: {
            label: 'Polub cel'
          },
          unlike: {
            label: 'Cofnij polubienie'
          },
          share: {
            label: 'Udostępnij cel'
          },
          more: {
            label: 'Więcej akcji'
          }
        },
        goalBottomSheet: {
          pauseGoal: 'Zatrzymaj cel',
          goToGoalEditor: 'Idź do edytora celu',
          goToSkillGraphEditor: 'Otwórz mapę myśli',
          goToGoalUsers: 'Idź do użytkowników celu',
          deleteGoal: 'Usuń cel',
          unsubscribeGoal: 'Zrezygnuj z realizacji celu',
          subscribeGoal: 'Dodaj cel do realizacji',
          resumeGoal: 'Wznów cel',
          deleteGoalConfirmationHeader: 'Czy na pewno chcesz usunąć ten cel?',
          deleteGoalConfirmationText:
            'Ta akcja jest nieodwracalna. Po usunięciu celu nie będziesz mieć już do niego dostępu.'
        },
        markdownEditor: {
          expand: 'Rozwiń',
          done: 'Gotowe'
        },
        skillGraphEditor: {
          back: 'Wróć',
          editNode: 'Edytuj węzeł',
          label: 'Etykieta',
          labelPlaceholder: 'Etykieta węzła…',
          description: 'Opis',
          descriptionPlaceholder: 'Dodaj opis…',
          autoLayout: 'Auto Layout',
          generate: 'Generuj',
          save: 'Zapisz',
          saving: 'Zapisywanie...',
          saved: 'Zapisano!',
          saveError: 'Błąd!',
          publish: 'Publikuj',
          publishing: 'Publikowanie...',
          published: 'Opublikowano!',
          publishError: 'Publikacja nie powiodła się',
          loading: 'Ładowanie mapy myśli...',
          loadError: 'Nie udało się wczytać mapy myśli'
        },
        goalUnsubscribeModal: {
          title: 'Jesteś o krok od zakończenia realizacji celu',
          textWhenActive:
            'Zrezygnowanie z realizacji tego celu spowoduje utratę całego Twojego dotychczasowego postępu.',
          textWhenPaused:
            'Zrezygnowanie z realizacji tego celu spowoduje utratę całego Twojego dotychczasowego postępu. Jesteś pewien/pewna?',
          closeButton: 'Zamknij'
        },
        goalSubscriptionButtons: {
          subscribe: 'Rozpocznij realizację celu',
          subscriptionCost: 'Koszt realizacji celu',
          subscriptionCostFree: 'Darmowy',
          subscriptionCostRecurring: 'miesięcznie'
        },
        goToGoalSubscriptionButton: {
          goToSubscription: 'Przejdź do realizacji celu'
        },
        goalCardUsers: {
          completedBy: 'Ukończone przez {{count}} użytkowników'
        },
        mentorGoalCard: {
          activeChip: 'Aktywny',
          activeChipTooltip: 'Aktywni subskrybenci',
          changed: 'Dodane lub zmienione',
          errors: 'Błędy',
          removed: 'Usunięte',
          warnings: 'Ostrzeżenia'
        },
        goalSampleTasks: {
          title: 'PRZYKŁADOWE ZADANIA'
        },
        goalStatistics: {
          author: {
            label: 'Autor:'
          }
        },
        master: {
          name: 'Imprv AI',
          jobTitle: 'Twój osobisty asystent',
          about:
            'Jestem tutaj, aby wspierać Cię w realizacji Twoich celów. Potrafię odpowiadać na Twoje pytania, przydzielać zadania i przypominać o nich. Pomogę Ci również w zarządzaniu celami i ich realizowaniu. Wciąż się uczę, więc proszę miej do mnie cierpliwość.'
        },
        noActiveSubscriptionForGoal: {
          header: 'Niestety nie realizujesz aktualnie niniejszego celu',
          text: 'Zapisz się, aby otrzymać zadania z tego celu.',
          goToGoalButton: {
            text: 'Przejdź do strony celu'
          }
        },
        errorUnexpectedPlaceholder: {
          header: 'Ups!',
          backToHome: {
            text: 'Coś poszło nie tak.. Przejdź do strony głównej.',
            buttonText: 'Przejdź do strony głównej'
          },
          reload: {
            text: 'Coś poszło nie tak.. Odśwież stronę i spróbuj ponownie.',
            buttonText: 'Odśwież stronę'
          }
        },
        errorUnauthorizedPlaceholder: {
          header: 'Ups!',
          text: 'Nie masz uprawnień do wyświetlenia tej strony.',
          backToHomeButton: {
            text: 'Przejdź do strony głównej'
          }
        },
        errorNotFoundPlaceholder: {
          header: 'Ups!',
          text: 'Nie znaleziono strony.',
          backToHomeButton: {
            text: 'Przejdź do strony głównej'
          }
        },
        goalUnavailablePlaceholder: {
          header: 'Nie możemy wyświetlić tej strony',
          signedOutText:
            'Zaloguj się na konto, które może mieć dostęp, albo poproś o dostęp osobę, która udostępniła Ci link.',
          signedInText:
            'Jeśli masz inne konto, które może mieć dostęp, przeloguj się. Możesz też poprosić o dostęp osobę, która udostępniła Ci link.',
          loginButton: {
            text: 'Zaloguj się'
          },
          switchAccountButton: {
            text: 'Przeloguj się'
          }
        },
        placeholderNoProfileAboutMe: {
          text: 'Nie ma jeszcze dodanego opisu'
        },
        placeholderNoProfileLinks: {
          text: 'Nie ma jeszcze dodanych linków do mediów społecznościowych i innych stron'
        },
        placeholderNoCompletedGoals: {
          textWithWho: '{{who}} nie ukończył/a jeszcze żadnych celów.',
          text: 'Nie masz jeszcze żadnych ukończonych celów.',
          exploreGoalsButton: {
            text: 'Eksploruj cele'
          }
        },
        placeholderNoConversationMessages: {
          text: 'Ta rozmowa nie ma jeszcze żadnych wiadomości'
        },
        placeholderNoConversations: {
          text: 'Kiedy zapiszesz się do nowego celu, pojawi się tutaj nowy czat.'
        },
        placeholderNoConversationTasks: {
          text: 'Nie masz jeszcze żadnych zadań'
        },
        placeholderNoCreatedGoals: {
          textWithWho: '{{who}} nie ma jeszcze żadnych utworzonych celów.',
          text: 'Nie masz jeszcze żadnych utworzonych przez siebie celów.',
          createButton: {
            text: 'Stwórz własny cel'
          },
          placeholder: 'Na razie ta opcja jest niedostępna.'
        },
        placeholderNoLikedGoals: {
          text: 'Nie masz jeszcze żadnych polubionych celów.',
          exploreGoalsButton: {
            text: 'Eksploruj cele'
          }
        },
        placeholderNoNotifications: {
          text: 'Nie masz żadnych notyfikacji.'
        },
        placeholderNoStreams: {
          exploreGoalsButton: {
            text: 'Eksploruj cele'
          }
        },
        placeholderNoSubscribedGoals: {
          text: 'Nie realizujesz jeszcze żadnego celu.',
          exploreGoalsButton: {
            text: 'Eksploruj cele'
          }
        },
        placeholderNothingFound: {
          text: 'Ups... nic nie znalazłem'
        },
        progressLegend: {
          title: 'PASEK POSTĘPU',
          label: 'To jest twój postęp w dążeniu do celu:',
          entities: {
            done: 'Ukończone zadanie.',
            todo: 'Zadanie do zrobienia.',
            pending: 'Zadanie aktywne, które jest aktualnie wyświetlane na stronie głównej.',
            cancelled: 'Zadanie anulowane.'
          },
          closeButton: {
            text: 'Zamknij'
          }
        },
        mainMenu: {
          links: {
            explore: 'Eksploruj',
            create: 'Stwórz',
            collapse: 'Zwiń'
          }
        },
        missingFeature: {
          text: '{{what}} jeszcze nie istnieje'
        },
        searchBar: {
          placeholder: 'Wyszukaj',
          disabledPlaceholder: 'Wyszukiwanie niedostępne w trakcie gry'
        },
        chatbotEntryEditor: {
          qa: {
            question: 'Pytanie',
            answer: 'Odpowiedź'
          },
          confirmButton: {
            text: 'Zapisz'
          }
        },
        chatbotSettingsEditor: {
          responseThreshold: {
            label:
              'W jakim zakresie Chatbot powinien automatycznie odpowiadać na pytania użytkowników?'
          },
          alarmPhrases: {
            label: 'Zapisz frazy, które oznaczą wiadomość jako wymagającą Twojej uwagi'
          }
        },
        chatbotSettingsPhraseInput: {
          newPlaceholder: 'Wpisz frazę (np. słabo się czuję)',
          deleteButton: {
            text: 'Usuń'
          }
        },
        affiliateLink: {
          label: 'Twój link afiliacyjny',
          copy: 'Skopiuj link afiliacyjny',
          copied: 'Skopiowano!'
        },
        earningsRange: {
          currentMonth: 'Ten miesiąc',
          lastMonth: 'Poprzedni miesiąc',
          last6Months: 'Ostatnie 6 miesięcy'
        },
        navBar: {
          homepage: 'Strona główna',
          subscriptions: 'Moje realizowane cele',
          create: 'Utwórz',
          explore: 'Eksploruj',
          messages: 'Wiadomości',
          profile: 'Mój profil',
          notifications: 'Powiadomienia',
          tasks: 'Biblioteka zadań',
          goals: 'Biblioteka celów',
          earnings: 'Zarobki',
          moreBottomSheetTitle: 'Idź do'
        },
        taskActionsBottomSheet: {
          editTask: 'Edytuj zadanie',
          deleteTask: 'Usuń zadanie',
          cloneTask: 'Duplikuj zadanie',
          cloneTaskPrefix: '(Kopia)',
          deleteConfirmationHeader: 'Czy na pewno chcesz usunąć to zadanie?',
          deleteConfirmationText:
            'Ta akcja jest nieodwracalna. Po usunięciu zadania nie będzie do niego dostępu.'
        },
        deleteErrorBottomSheet: {
          acknowledgeButton: 'Rozumiem'
        }
      },
      pages: {
        affiliate: {
          title: 'Twoje zarobki',
          header: 'Zarobki afiliacyjne',
          subtitle:
            'Prowizja od wszystkiego, co sprzedasz, i od każdego pozyskanego leada – bezpośrednio i z naszą pomocą.',
          empty: {
            title: 'Brak leadów',
            description:
              'Gdy osoba pozyskana przez Ciebie kupi coś z Twojego linku, jej zamówienia pojawią się tutaj.'
          },
          error: {
            title: 'Nie udało się wczytać zarobków',
            description: 'Coś poszło nie tak podczas wczytywania zarobków. Spróbuj ponownie.'
          },
          summary: {
            title: 'Podsumowanie',
            leads: 'Pozyskane leady',
            orders: 'Zamówienia'
          },
          chart: {
            title: 'Leady w czasie',
            leads: 'Leady'
          },
          table: {
            title: 'Zamówienia Twoich leadów',
            date: 'Data i godzina',
            buyer: 'Kupujący',
            orderId: 'ID zamówienia',
            kind: 'Rodzaj',
            product: 'Produkt',
            landingPage: 'Strona produktu',
            openLandingPage: 'Otwórz',
            kindFirst: 'Pierwsze zamówienie',
            kindNext: 'Kolejne zamówienie',
            typeGoal: 'Cel',
            typePackage: 'Pakiet'
          }
        },
        assignments: {
          loadingConversation: 'Ładowanie rozmowy...'
        },
        garminConnections: {
          title: 'Połączenia z Garmin',
          loaderText: 'wczytywanie twoich połączeń z Garmin',
          addNew: 'Dodaj nowe połączenie z Garmin',
          yourAreConnected: 'Jesteś połączony z Garmin',
          yourConnectionIsPending: 'Twoje połączenie z Garmin jest w trakcie akceptacji',
          noActiveOrPending: 'Nie masz żadnych połączeń z Garmin'
        },
        garminConnectionAuthorize: {
          loaderText: 'autoryzacja twojego połączenia z Garmin',
          authorizedText: 'Twoje połączenie z Garmin zostało autoryzowane'
        },
        account: {
          yourAccount: 'Twoje konto',
          tabs: {
            settings: 'Ustawienia',
            earnings: 'Monetyzacja',
            orders: 'Zamówienia'
          },
          orders: {
            header: 'Twoje zamówienia',
            tableTitle: 'Historia zamówień',
            tableProduct: 'Produkt',
            tableDate: 'Data',
            tableAmount: 'Kwota',
            tableStatus: 'Status',
            typeGoal: 'Cel',
            typePackage: 'Pakiet',
            typeCoins: 'IMPRV Coins',
            coins: 'IMPRV Coins',
            status: {
              active: 'Aktywne',
              refund: 'Zwrócone',
              expired: 'Wygasłe'
            },
            emptyTitle: 'Brak zamówień',
            emptyDescription: 'Cele i pakiety, które kupisz, pojawią się tutaj.',
            invoice: {
              column: 'Faktura',
              button: 'Pobierz fakturę',
              pending: 'Faktura jest generowana. Spróbuj ponownie za chwilę.',
              missingDetails: 'Najpierw uzupełnij dane do faktury w ustawieniach konta.',
              failed: 'Nie udało się wystawić faktury – sprawdź dane do faktury (NIP).',
              detailsRequiredBanner: 'Aby pobierać faktury, najpierw uzupełnij dane do faktury.',
              detailsRequiredLink: 'Uzupełnij dane do faktury'
            }
          },
          mentorEarnings: {
            header: 'Zarobki jako mentor',
            summaryTitle: 'Podsumowanie',
            chartTitle: 'Wypłata w czasie',
            tableTitle: 'Sprzedane cele',
            netTotal: 'Zarobek netto',
            grossTotal: 'Przychód brutto',
            unitsSold: 'Sprzedane sztuki',
            chartNet: 'Zarobek netto',
            tableDate: 'Data',
            tableGoal: 'Cel',
            tableBuyer: 'Kupujący',
            tableGross: 'Zapłacono',
            tableNet: 'Twoja wypłata',
            emptyTitle: 'Brak sprzedaży',
            emptyDescription: 'Gdy improverzy kupią Twoje cele, Twoje zarobki pojawią się tutaj.',
            errorTitle: 'Nie udało się wczytać zarobków',
            errorDescription: 'Coś poszło nie tak podczas wczytywania zarobków. Spróbuj ponownie.'
          },
          garminConnectionsButton: {
            text: 'Połączenia z Garmin'
          },
          usages: {
            certification: 'Pole wykorzystywane w certyfikacie'
          },
          sections: {
            affiliatePercentages: {
              title: 'Twoje procenty',
              referralCode: {
                title: 'Kod polecający',
                hint: 'Dodaj ?ref= z tym kodem do dowolnego linku celu lub pakietu.',
                copy: 'Kopiuj',
                copied: 'Skopiowano'
              },
              rates: {
                title: 'Stawki prowizji',
                firstDirect: 'Bezpośrednio – pierwszy zakup',
                firstAssisted: 'Z naszą pomocą – pierwszy zakup',
                residualDirect: 'Bezpośrednio – kolejne zakupy',
                residualAssisted: 'Z naszą pomocą – kolejne zakupy',
                mentorShareTitle: 'Twój udział mentora',
                mentorMarginIndependent: 'Marża samodzielna',
                mentorMarginAssisted: 'Marża niesamodzielna',
                mentorMarginHint:
                  'Twój udział w cenie celu: marża samodzielna, gdy sprzedajesz sam, i niesamodzielna, gdy sprzedaż napędził afiliator/imprv – stosujemy ją automatycznie w edytorze ceny celu.',
                notSet: '–'
              },
              explanation:
                '„Bezpośrednio” dotyczy sytuacji, gdy kupujący trafia przez Twój link; każdy inny zakup leada, którego pozyskałeś, rozliczamy stawką „z naszą pomocą”. Warunki ustala imprv.'
            },
            loginInfo: {
              title: 'Informacje logowania',
              editors: {
                login: {
                  title: 'Logowanie',
                  loginMethod: 'Logujesz się za pomocą:',
                  logoutButtonText: 'Wyloguj',
                  loginMethods: {
                    PASSWORD: 'Adresu email i hasła',
                    GOOGLE: 'Konta Google'
                  }
                }
              }
            },
            invoice: {
              header: 'Dane do faktury',
              description:
                'Służą do wystawiania faktur za Twoje zakupy. Wymagane tylko wtedy, gdy poprosisz o fakturę.',
              editors: {
                buyerType: 'Typ nabywcy',
                buyerTypeOptions: {
                  COMPANY: 'Firma',
                  PRIVATE: 'Osoba prywatna'
                },
                name: 'Nazwa / nazwa firmy',
                namePlaceholder: 'np. ACME Sp. z o.o. lub Jan Kowalski',
                taxId: 'NIP',
                taxIdPlaceholder: 'np. 1234563218',
                taxIdError: 'Nieprawidłowy NIP',
                street: 'Ulica',
                streetNumber: 'Numer budynku / lokalu',
                city: 'Miejscowość',
                postCode: 'Kod pocztowy',
                country: 'Kraj',
                countryPlaceholder: 'PL',
                tooLongError: 'Wartość jest zbyt długa'
              }
            },
            accountInfo: {
              title: 'Konto',
              editors: {
                account: {
                  email: 'Adres email',
                  emailInfo:
                    'Obecnie nie umożliwiamy zmiany adresu e-mail. W razie jakichkolwiek problemów skontaktuj się z nami pod adresem hello@imprv.ai',
                  username: 'Nazwa użytkownika',
                  usernameInfo:
                    'Obecnie nie umożliwiamy zmiany nazwy użytkownika. W razie jakichkolwiek problemów skontaktuj się z nami pod adresem hello@imprv.ai',
                  firstName: 'Imię',
                  firstNamePlaceholder: 'Wpisz imię',
                  firstNameMaxLengthError: 'Imię musi być krótsze niż 200 znaków',
                  lastName: 'Nazwisko',
                  lastNamePlaceholder: 'Wpisz nazwisko',
                  lastNameMaxLengthError: 'Nazwisko musi być krótsze niż 200 znaków',
                  gender: 'Płeć',
                  genderOptions: {
                    MALE: 'Mężczyzna',
                    FEMALE: 'Kobieta',
                    NON_BINARY: 'Niebinarny',
                    DONT_WANT_TO_DISCLOSE: 'Nie chcę podawać'
                  },
                  genderInfo:
                    'Tę informację wykorzystamy wyłącznie po to, aby trafniej dobierać formy zwracania się do Ciebie (np. zaimki). Podanie jej jest opcjonalne — jeśli wolisz tego nie ujawniać, wybierz "Nie chcę podawać"',
                  saveButtonText: 'Zapisz'
                }
              }
            },
            imprvCoins: {
              title: 'Historia Imprv Coinów',
              empty: 'Nie masz jeszcze historii imprv coinów',
              loadFailed: 'Nie udało się wczytać historii imprv coinów – odśwież stronę',
              dataIncomplete:
                'Część danych o imprv coinach jest chwilowo niedostępna – historia poniżej jest niekompletna, dlatego saldo nie jest pokazywane',
              columns: {
                number: 'Liczba',
                description: 'Opis',
                date: 'Data',
                expiry: 'Ważność'
              },
              acquisitionKinds: {
                PURCHASE: 'Zakup',
                BONUS: 'Bonus'
              },
              requestKinds: {
                run: 'Generator',
                transcription: 'Dyktowanie',
                image_edit: 'Edycja obrazu',
                mentorbot_reply: 'Odpowiedź Mentorbota',
                block_evaluation: 'Ocena odpowiedzi',
                unknown: 'Zużycie AI'
              },
              perpetual: 'bezterminowo',
              summary: {
                remaining: 'Pozostało',
                byExpiry: 'Ważne do',
                perpetual: 'bezterminowo'
              },
              buyCta: 'Kup coiny'
            }
          }
        },
        accountProfile: {
          createdGoals: 'Utworzone cele',
          completedGoals: 'Ukończone cele',
          joinedAt: 'Data dołączenia',
          aboutMe: 'O mnie',
          moreInfo: {
            title: 'Więcej informacji'
          }
        },
        chatbots: {
          editor: {
            title: 'Edytor chatbota',
            trainingTabTitle: 'Trenowanie',
            settingsTabTitle: 'Ustawienia',
            testingTabTitle: 'Przetestuj',
            question: 'Pytanie',
            questionPlaceholder: 'Wpisz pytanie',
            answer: 'Odpowiedź',
            generateResponse: 'Wygeneruj odpowiedź',
            generatingResponse: 'Zapytanie w trakcie przetwarzania...',
            thanksForFeedback: 'Dziękujemy!',
            howDoYouRateTheResponse: 'Jak oceniasz odpowiedź Chatbota?',
            clear: 'Wyczyść',
            chatbotCouldNotRespond:
              'Dane dostępne w zakładce "Trenowanie" nie są wystarczające dla Chatbota, aby odpowiedzieć na to pytanie. Jeśli użytkownik zadałby takie pytanie, zostałoby ono przekierowane do Ciebie',
            addButton: {
              text: 'Dodaj pytanie'
            },
            actions: {
              delete: 'Usuń pytanie'
            }
          }
        },
        conversations: {
          states: {
            paused: {
              title: 'Zatrzymaj cel',
              resumeButton: {
                text: 'Wznów'
              }
            },
            completed: {
              title: 'Ta rozmowa została zarchiwizowana, ponieważ cel został ukończony'
            }
          }
        },
        home: {
          header: 'Strona główna',
          welcome: 'Witaj {{name}}!',
          welcomeSubtext:
            'To co kontynuujemy naukę? Dzisiaj dużo przed nami, a będzie jeszcze więcej!',
          yourGoalsTab: 'Twoje cele ({{count}})',
          messagesTab: 'Wiadomości ({{count}})',
          noActiveGoalSubscriptions: {
            header: 'Aktualnie nie realizujesz żadnego celu',
            button: 'Znajdź cel do realizacji'
          },
          neverHadActiveGoalSubscriptions: {
            header: 'Witaj w Imprv👋',
            text: 'Znajdź pierwszy cel dla siebie',
            button: 'Znajdź cel do realizacji'
          },
          goalSubscriptionsArePaused: {
            header: 'Twoje cele są zapauzowane ☕',
            text: 'Wznów swoje cele, aby otrzymać kolejne zadania',
            button: 'Przejdź do celów'
          }
        },
        taskEditor: {
          title: 'Edytor zadania',
          preview: 'Podgląd zadania',
          addTaskButton: {
            text: 'Dodaj zadanie'
          },
          addBlockButton: {
            text: 'nowy blok'
          },
          addBlockGroupButton: {
            text: 'Dodaj serię'
          },
          titleField: {
            text: 'Tytuł',
            placeholder: 'Wpisz nazwę zadania'
          },
          createBlock: {
            text: 'Utwórz blok'
          },
          blocksSection: {
            text: 'Bloki'
          },
          blocks: {
            tabs: {
              basic: 'Podstawowe',
              active: 'Aktywne',
              training: 'Trening',
              trainingExclusiveness:
                'W ramach jednego zadania można używać jedynie jednego rodzaju bloku treningowego',
              games: 'Gry',
              measurements: 'Pomiary'
            },
            actions: {
              clone: 'Duplikuj',
              delete: 'Usuń'
            },
            groupEditor: {
              title: 'Seria',
              types: {
                REPETITIONS: 'Ilość powtórzeń'
              }
            },
            staticEditor: {
              name: 'Podstawowy',
              title: 'Podstawowy'
            },
            staticAttachmentsEditor: {
              attachmentTextPlaceholder: 'Wpisz komentarz',
              addAttachment: 'Dodaj kolejny załącznik',
              unnamedFile: 'Plik bez nazwy'
            },
            staticAttachmentsSlide: {
              downloadFile: 'Pobierz plik'
            },
            inputFileUploadEditor: {
              name: 'Przesyłanie plików',
              title: 'Blok przesyłania plików',
              fileType: 'Typ pliku',
              fileInstructions: 'Instrukcje dla tego pliku...',
              noInstructions: 'Brak instrukcji',
              addFileUpload: 'Dodaj kolejny załącznik'
            },
            inputFileUploadSlide: {
              uploadPlaceholder: 'Wybierz plik...'
            },
            inputTimerEditor: {
              name: 'Timer',
              title: 'Timer',
              setTime: 'Ustaw czas:'
            },
            inputBooleanEditor: {
              name: 'Tak lub Nie',
              title: 'Tak lub Nie',
              choices: {
                true: 'Tak',
                false: 'Nie'
              }
            },
            inputTextEditor: {
              name: 'Tekst',
              title: 'Tekst'
            },
            inputNumberEditor: {
              name: 'Liczba',
              typeTitle: 'Typ',
              typeInteger: 'Liczba całkowita',
              typeFloat: 'Liczba z miejscami po przecinku'
            },
            inputDateTimeEditor: {
              name: 'Data i czas',
              typeTitle: 'Typ',
              typeDate: 'Data',
              typeTime: 'Czas',
              typeDateTime: 'Data i czas'
            },
            inputSingleChoiceEditor: {
              name: 'Jednokrotny wybór',
              title: 'Jednokrotny wybór',
              addOption: 'Dodaj opcję',
              maxOptionCountsReached: 'dodano maksymalną ilość opcji {{maxCount}}'
            },
            inputMultipleChoiceEditor: {
              name: 'Wielokrotny wybór',
              title: 'Wielokrotny wybór',
              addOption: 'Dodaj opcję',
              maxOptionCountsReached: 'dodano maksymalną ilość opcji {{maxCount}}'
            },
            inputRatingEditor: {
              name: 'Ocena',
              title: 'Ocena',
              chooseMaxRating: 'Wybierz maksymalną ocenę',
              maxValue: 'Maksymalna wartość',
              minValueText: 'Gdzie 1 oznacza',
              minValueTextPlaceholder: 'Wpisz wyjaśnienie',
              maxValueText: 'Gdzie {{max}} oznacza',
              maxValueTextPlaceholder: 'Wpisz wyjaśnienie'
            },
            inputRangeEditor: {
              name: 'Przedział',
              title: 'Przedział'
            },
            inputBricksEditor: {
              notAvailableOnMobile: 'Edytor klocków niedostępny na urządzeniach mobilnych',
              useDesktopVersion: 'Użyj wersji komputerowej, aby edytować klocki',
              addCategory: 'dodaj kategorię',
              addElement: 'dodaj element',
              category: 'Kategoria',
              categoryPlaceholder: 'Nazwa kategorii',
              hasElements: 'ma {{count}} elementów',
              deleteBrickConfirmationHeader: 'Czy na pewno chcesz usunąć ten element?',
              deleteBrickConfirmationText:
                'Ta akcja jest nieodwracalna. Po usunięciu elementu nie będzie on dostępny w tej kategorii.',
              deleteCategoryConfirmationHeader: 'Czy na pewno chcesz usunąć tę kategorię?',
              deleteCategoryConfirmationText:
                'Ta akcja jest nieodwracalna. Po usunięciu kategorii nie będzie ona dostępna w zadaniu.',
              bicksContentItemTextPlaceholder:
                'Wpisz instrukcje do gry (np. "Przyporządkuj klocki do kategorii")',
              editGame: 'Edytuj Grę'
            },
            gameEditorHeader: {
              done: 'Gotowe'
            },
            inputTrainingEditor: {
              typeYoga: 'Joga',
              typePilates: 'Pilates',
              typeCardio: 'Kardio',
              typeStrength: 'Ćwiczenia siłowe',
              typeLapSwimming: 'Pływanie',
              typeCycling: 'Jazda na rowerze',
              typeRunning: 'Bieganie',
              intensity: {
                title: 'Intensywność',
                typeWarmup: 'Rozgrzewka',
                typeNormal: {
                  INPUT_TRAINING_RUNNING: 'Bieg',
                  INPUT_TRAINING_CYCLING: 'Jazda rowerem',
                  INPUT_TRAINING_LAP_SWIMMING: 'Pływanie',
                  INPUT_TRAINING_STRENGTH: 'Trening',
                  INPUT_TRAINING_CARDIO: 'Trening',
                  INPUT_TRAINING_YOGA: 'Trening',
                  INPUT_TRAINING_PILATES: 'Trening'
                },
                typeCooldown: 'Wyciszenie'
              },
              duration: {
                title: 'Kiedy zakończyć?',
                titleReadonly: 'Zakończ',
                unit: 'Jednostka',
                types: {
                  DURATION_CALORIES: 'Po spaleniu',
                  DURATION_DISTANCE: 'Po pokonaniu dystansu',
                  DURATION_HEART_RATE: 'Po osiągnięciu tętna',
                  DURATION_OPEN: 'Po kliknięciu przycisku Lap',
                  DURATION_POWER: 'Po osiągnięciu',
                  DURATION_REPS: 'Po wykonaniu',
                  DURATION_SWIM_DISTANCE: 'Po pokonaniu',
                  DURATION_TIME: 'Po upływie czasu'
                }
              },
              target: {
                title: 'Jaki jest cel?',
                titleReadonly: 'Cel',
                types: {
                  none: 'Brak',
                  TARGET_HEART_RATE: 'Tętno w strefie',
                  TARGET_PACE: 'Tempo',
                  TARGET_POWER: 'Moc w strefie',
                  TARGET_RUNNING_CADENCE: 'Kadencja biegowa',
                  TARGET_SPEED: 'Prędkość'
                }
              }
            },
            gamePuzzleEditor: {
              name: 'Puzzle',
              title: 'Puzzle'
            },
            gameBricksEditor: {
              name: 'Klocki',
              title: 'Klocki'
            },
            gameFlashcardsEditor: {
              name: 'Gra fiszki',
              title: 'Fiszki',
              flashcardsContentItemTextPlaceholder:
                'Wpisz instrukcje do gry (np. "Odpowiedz na pytania na fiszkach")',
              assertionType: {
                title: 'Typ asercji',
                text: 'Tekst',
                integer: 'Liczba całkowita',
                decimal: 'Liczba dziesiętna',
                date: 'Data',
                datetime: 'Data i czas',
                boolean: 'Prawda/Fałsz',
                ai: 'AI evaluator'
              }
            },
            gameConnectorEditor: {
              name: 'Połącz elementy',
              title: 'Połącz elementy'
            },
            gameBlanksEditor: {
              name: 'Wypełnij luki',
              title: 'Gra Wypełnij Luki',
              addText: 'dodaj tekst',
              addBlank: 'dodaj lukę',
              editGame: 'Edytuj Grę',
              textInputPlaceholder: 'Sprawdź czy uda Ci się odgadnąć wszystkie ukryte słowa',
              blankWordPlaceholder: 'Słowo do uzupełnienia',
              blankExplanationPlaceholder: 'Wyjaśnienie luki',
              multipleOccurrencesMessage: 'Ta luka pojawia się wielokrotnie w tekście.',
              noMatchFoundMessage: 'Nie znaleziono dopasowania w tekście!',
              blankRemovedMessage: 'Luka została usunięta z tekstu!',
              deleteProblemConfirmationHeader: 'Czy na pewno chcesz usunąć ten problem?',
              deleteProblemConfirmationText:
                'Ta akcja jest nieodwracalna. Po usunięciu problemu nie będzie on dostępny w zadaniu.',
              deleteBlankConfirmationHeader: 'Czy na pewno chcesz usunąć tę lukę?',
              deleteBlankConfirmationText:
                'Ta akcja jest nieodwracalna. Po usunięciu luki nie będzie ona dostępna w zadaniu.',
              problemTextPlaceholder: 'Podaj swój tekst',
              reloadingGeneratedContent: 'Wykryto nową zawartość. Ładowanie…'
            },
            measurementsBloodResultEditor: {
              name: 'Wynik krwi',
              title: 'Wynik krwi'
            },
            measurementsGrowthChart: {
              name: 'Siatka centylowa',
              title: 'Siatka centylowa'
            }
          },
          validator: {
            title: 'Walidator i Optymalizator',
            subtitle: {
              hasErrors:
                'Niestety wykryliśmy kilka błędów, które należy zaadresować zanim publikacja będzie możliwa.',
              clean: 'Brawo! Nie ma żadnych błędów!',
              cleanWithSuggestions: 'Brak błędów blokujących — ale są wskazówki warte przejrzenia.'
            },
            passedRulesCount_one: '{{count}} reguła zaliczona! Brawo!',
            passedRulesCount_few: '{{count}} reguły zaliczone! Brawo!',
            passedRulesCount_many: '{{count}} reguł zaliczonych! Brawo!',
            sectionErrorsBlanks_one: '{{count}} błąd! W grze luki: „{{taskTitle}}”',
            sectionErrorsBlanks_few: '{{count}} błędy! W grze luki: „{{taskTitle}}”',
            sectionErrorsBlanks_many: '{{count}} błędów! W grze luki: „{{taskTitle}}”',
            sectionErrorsBricks_one: '{{count}} błąd! W grze klocki: „{{taskTitle}}”',
            sectionErrorsBricks_few: '{{count}} błędy! W grze klocki: „{{taskTitle}}”',
            sectionErrorsBricks_many: '{{count}} błędów! W grze klocki: „{{taskTitle}}”',
            sectionErrorsInputText_one: '{{count}} błąd! W pytaniu otwartym: „{{taskTitle}}”',
            sectionErrorsInputText_few: '{{count}} błędy! W pytaniu otwartym: „{{taskTitle}}”',
            sectionErrorsInputText_many: '{{count}} błędów! W pytaniu otwartym: „{{taskTitle}}”',
            sectionErrorsFlashcards_one: '{{count}} błąd! W fiszkach: „{{taskTitle}}”',
            sectionErrorsFlashcards_few: '{{count}} błędy! W fiszkach: „{{taskTitle}}”',
            sectionErrorsFlashcards_many: '{{count}} błędów! W fiszkach: „{{taskTitle}}”',
            sectionWarningsTitle_one: '{{count}} ostrzeżenie!',
            sectionWarningsTitle_few: '{{count}} ostrzeżenia!',
            sectionWarningsTitle_many: '{{count}} ostrzeżeń!',
            sectionTipsTitle_one: '{{count}} pomysł na ulepszenie',
            sectionTipsTitle_few: '{{count}} pomysły na ulepszenia',
            sectionTipsTitle_many: '{{count}} pomysłów na ulepszenia',
            strategyTaskHeader_one: '{{count}} błąd w zadaniu: „{{taskTitle}}”',
            strategyTaskHeader_few: '{{count}} błędy w zadaniu: „{{taskTitle}}”',
            strategyTaskHeader_many: '{{count}} błędów w zadaniu: „{{taskTitle}}”',
            rules: {
              blockNoProblems: 'Blok nie ma żadnych problemów',
              problemEmpty: 'Problem nie ma tekstu',
              problemNoBlanks: 'Problem nie ma luki',
              blankEmptyWord: 'Luka w problemie nie ma słowa',
              blankNoOccurrence: 'Luka „{{word}}” nie znajduje się w tekście problemu',
              blankDuplicateWord:
                'Inna luka w tym problemie używa tego samego słowa — ocenianie będzie niejednoznaczne',
              blockAssertionMissing: 'Gra nie ma jeszcze zdefiniowanych poprawnych odpowiedzi',
              blankMultipleOccurrences: 'Ta luka ma więcej niż jedno aktywne wystąpienie w tekście',
              blankNoExplanation: 'Dodaj wyjaśnienie, aby pomóc uczącym się',
              blockNoCategories: 'Blok nie ma żadnych kategorii',
              blockNoBricks: 'Blok nie ma żadnych klocków',
              categoryEmptyName: 'Kategoria nie ma nazwy',
              brickEmpty: 'Klocek jest pusty — dodaj tekst lub media',
              categoryNoBricks: 'Kategoria nie ma przypisanych klocków',
              brickNotAssigned: 'Klocek nie jest przypisany do żadnej kategorii',
              assocOrphanReference:
                'Poprawne odpowiedzi odwołują się do nieistniejącej kategorii lub klocka',
              brickInMultipleCategories: 'Klocek jest przypisany do więcej niż jednej kategorii',
              duplicateBrickText: 'Inny klocek w tej samej kategorii używa tego samego tekstu',
              duplicateBrickTextOtherCategory:
                'Inny klocek w innej kategorii używa tego samego tekstu',
              identicalBrickOtherCategory:
                'Identyczny klocek jest w innej kategorii — zadanie nierozwiązywalne',
              blockSingleCategory: 'Dodaj kolejną kategorię, aby sortowanie miało sens',
              categorySingleBrick: 'Ta kategoria ma tylko jeden klocek — rozważ dodanie kolejnych',
              criteriaEmptyList: 'Dodaj co najmniej jedno kryterium oceny',
              criterionEmptyTitle: 'To kryterium nie ma tytułu',
              criterionEmptyDescription: 'To kryterium nie ma opisu'
            }
          }
        },
        goalEditorHub: {
          basicInfo: {
            title: 'Podstawowe informacje'
          },
          landingPage: {
            title: 'Landing Page',
            empty: 'Zaktualizuj landing page'
          },
          tasks: {
            title: 'Zadania',
            summary: '{{paths}} sekcji i {{tasks}} zadań'
          },
          tabs: {
            content: 'Treść',
            monetization: 'Monetyzacja',
            users: 'Użytkownicy',
            visibility: 'Widoczność'
          },
          monetization: {
            priceSectionTitle: 'Cena',
            couponsSectionTitle: 'Kupony i zniżki',
            salesSectionTitle: 'Sprzedaż',
            earnings: {
              summaryTitle: 'Podsumowanie',
              netTotal: 'Zarobek netto',
              grossTotal: 'Przychód brutto',
              unitsSold: 'Sprzedane sztuki',
              chartTitle: 'Zarobek netto w czasie',
              chartNet: 'Netto',
              tableTitle: 'Sprzedaż',
              tableDate: 'Data',
              tableBuyer: 'Kupujący',
              tableGross: 'Zapłacono',
              tableNet: 'Twoja wypłata',
              emptyTitle: 'Brak sprzedaży',
              emptyDescription: 'Gdy uczestnicy kupią ten cel, Twoje zarobki pojawią się tutaj.',
              errorTitle: 'Nie udało się wczytać zarobków',
              errorDescription: 'Coś poszło nie tak podczas wczytywania danych. Spróbuj ponownie.'
            },
            priceCardTitle: 'CENA ZA KURS',
            priceForImproverLabel: 'Cena jaką zobaczy improver',
            amountForMentorLabel: 'Na rękę dla Mentora',
            amountForMentorSaleModeTitle: 'Rodzaj sprzedaży',
            amountForMentorDirect: 'Sprzedaż samodzielna',
            amountForMentorAssisted: 'Sprzedaż niesamodzielna',
            feesLabel: 'Marża i podatki',
            publishChanges: 'Publikuj zmiany',
            createPrice: 'Utwórz cenę'
          },
          visibility: {
            cardTitle: 'WIDOCZNOŚĆ CELU',
            publicLabel: 'Publiczny',
            statusPrivate:
              'Cel jest prywatny – widzą go tylko osoby z dostępem. Nie pojawia się w Explore.',
            statusPublic:
              'Cel jest publiczny – jego stronę zobaczy każdy, kto ma link. Cel jest widoczny w Explore.',
            lockedHint: 'Upublicznianie celów jest na razie dostępne dla wybranych kont.',
            makePublicModal: {
              title: 'Upublicznić cel?',
              body: 'Cel pojawi się w Explore, a jego stronę zobaczy każdy, kto ma link. Zmiana wejdzie w życie przy najbliższej publikacji celu.',
              confirm: 'Upublicznij cel'
            },
            makePrivateModal: {
              title: 'Ukryć cel?',
              body: 'Cel zniknie z Explore, a nowe osoby stracą dostęp do jego strony. Obecni uczestnicy zachowują dostęp. Zmiana wejdzie w życie przy najbliższej publikacji celu.',
              confirm: 'Ukryj cel'
            },
            toastPublic: 'Zapisano – cel stanie się publiczny przy publikacji',
            toastPrivate: 'Zapisano – cel stanie się prywatny przy publikacji',
            effectiveOnPublish: 'Zmiany widoczności wchodzą w życie przy publikacji celu.',
            loadFailed: 'Nie udało się wczytać ustawień widoczności strony.',
            pendingPublishHint:
              'Zmiana widoczności celu czeka na publikację – dostęp do strony celu odpowiada wersji opublikowanej.',
            landingPageAccess: {
              cardTitle: 'WIDOCZNOŚĆ STRONY CELU',
              dropdownTitle: 'Kto może otworzyć stronę celu?',
              publicForced: 'Wszyscy – cel jest publiczny',
              publicHint: 'Publiczny cel jest też widoczny w Explore.',
              immediateNote: 'Zmiany tutaj działają od razu, bez publikacji.',
              toastSaved: 'Widoczność strony zapisana.',
              toastSavedListsKept:
                'Widoczność strony zapisana. Listy dostępu zostają zachowane i wrócą do działania po powrocie do tego trybu.',
              options: {
                NOBODY: 'Nikt – strona ukryta',
                SELECTED: 'Wybrane osoby i domeny',
                ANYONE: 'Każdy, kto ma link'
              }
            }
          },
          accessList: {
            title: 'LISTA DOSTĘPU',
            description:
              'Stronę celu otworzy każdy, kto ma adres e-mail z listy – albo adres w jednej z tych domen.',
            emailsGroupLabel: 'Adresy e-mail',
            domainsGroupLabel: 'Domeny',
            immediateNote: 'Zmiany tutaj działają od razu, bez publikacji.',
            inputPlaceholder: 'Dodaj adres e-mail albo domenę, np. acme.com…',
            addLabel: 'Dodaj',
            removeLabel: 'Usuń {{value}}',
            invalidEntry: 'Wpisz adres e-mail albo domenę.',
            duplicateEntry: 'Ten wpis już jest na liście.',
            toastSaved: 'Lista dostępu zapisana.'
          }
        },
        goalEditor: {
          title: 'Edytor celu',
          imageField: {
            text: 'miniatura',
            uploadButtonText: 'Dodaj miniaturę dla celu'
          },
          titleField: {
            text: 'nazwa celu',
            placeholder: 'Wpisz nazwę celu'
          },
          shortDescriptionField: {
            text: 'krótki opis',
            placeholder: 'Wpisz krótki opis'
          },
          descriptionField: {
            text: 'opis',
            placeholder: 'Wpisz opis'
          },
          categoriesField: {
            text: 'kategorie'
          },
          yamlField: {
            text: 'Landing Page',
            buttonText: 'Edytuj',
            saveButtonText: 'Zapisz zmiany'
          },
          chatbotField: {
            text: 'czatbot',
            summary: 'Ilość utworzonych par Q&A: {{count}}',
            validationError: 'Potrzebujesz co najmniej {{count}} par Q&A',
            publishLabel: 'Aktualizuj czatbota do już opublikowanego celu.',
            summaryDraft: 'Obecnie masz {{count}} szkiców par Q&A, które są nieopublikowane.'
          },
          strategyField: {
            text: 'strategia',
            summary: 'Ilość zadań dodanych do strategii: {{count}}',
            validationError: 'Potrzebujesz co najmniej {{count}} zadań w strategii',
            publishLabel: 'Aktualizuj strategię do już opublikowanego celu.',
            summaryDiff: 'Masz obecnie następujące nieopublikowane zmiany:',
            summaryPathsDraft: '{{count}} x zmienionych lub dodanych ścieżek',
            summaryPathsToDelete: '{{count}} x ścieżek do usunięcia',
            summaryNodesToDelete: '{{count}} x węzłów do usunięcia',
            summaryTasksDraft: '{{count}} x zmienionych lub dodanych zadań',
            summaryTaskBlocksDraft: '{{count}} x zmienionych lub dodanych bloków zadań',
            summaryActivationRulesDraft: '{{count}} x zmienionych lub dodanych reguł aktywacji'
          }
        },
        goals: {
          loader: 'Ładuję cele',
          filters: {
            all: 'Wszystkie cele',
            drafts: 'Szkice',
            published: 'Opublikowane',
            favourites: 'Ulubione'
          },
          filtersTitle: 'Wybierz cele do wyświetlenia',
          user: {
            placeholder: 'Wybierz zadanie z listy',
            mentorApproved: {
              approve: 'Zatwierdź',
              success: 'Feedback zatwierdzony i wysłany do ucznia',
              error: 'Nie udało się wysłać feedbacku. Spróbuj ponownie.',
              refreshError:
                'Feedback zatwierdzony, ale nie udało się odświeżyć widoku. Odśwież stronę.'
            },
            feedbackRequirementType: {
              REQUIRED: 'WYMAGANE',
              OPTIONAL: 'OPCJONALNE'
            }
          }
        },
        tasks: {
          loader: 'Ładuję zadania',
          orDivider: 'lub',
          returnToInbox: 'Wróć do Strony Głównej',
          closeAndExit: 'Wyjdź',
          filters: {
            all: 'Wszystkie zadania'
          },
          create: 'Nowe zadanie',
          filtersTitle: 'Wybierz zadania do wyświetlenia',
          endSlide: {
            label: {
              completed: 'ZADANIE UKOŃCZONE',
              almostCompleted: 'ZADANIE "PRAWIE" UKOŃCZONE',
              inProgress: 'ZADANIE W TRAKCIE',
              needsConsolidation: 'ZADANIE DO UTRWALENIA',
              notStarted: 'ZADANIE NIEROZPOCZĘTE'
            },
            heading: {
              excellent: 'Rewelacja! Masz opanowany cały materiał!',
              someSkipped: 'Całkiem nieźle, fajny postęp! Ale kilka bloków zostało pominiętych ;-)',
              consolidation: 'Warto wracać do trudniejszych bloków, wybierz jeden z nich.',
              notStarted: 'Czas zacząć! Przejdź przez bloki i zdobywaj swoje umiejętności.'
            },
            stats: {
              score: 'WYNIK',
              blocks: 'BLOKI',
              time: 'CZAS'
            },
            legend: {
              high: 'umiesz to',
              medium: 'prawie mamy pewność, że umiesz',
              low: 'musimy jeszcze potwierdzić',
              none: 'pominięte'
            },
            clickToGoToBlock: 'KLIKNIJ ABY PRZEJŚĆ DO BLOKU',
            nextStep: 'następny krok',
            nextStepHeading: {
              consolidation: 'W kolejnym zadaniu naszym celem jest utrwalenie materiału!',
              newChallenge: 'Czas na nowe wyzwanie! Poznaj kolejny temat.',
              keepGoing: 'Kontynuuj naukę! Następne zadanie pomoże Ci opanować materiał.',
              pickUpWhereLeftOff: 'Już sporo zrobione - czas dokończyć!',
              mastered:
                'Ten temat masz już opanowany! Wybierz inne zadanie z listy lub utrwal wiedzę.'
            }
          }
        },
        notifications: {
          title: 'Powiadomienia'
        },
        verifyEmail: {
          header: 'Zweryfikuj swój adres email',
          message:
            'Żeby się zalogować potwierdź swój adres email. Otwórz w wiadomość otrzymaną od Imprv i kliknij w link.',
          spamNote: 'Jeżeli nie widzisz wiadomości, sprawdź folder spam',
          backToLogin: 'Wróć do strony logowania'
        }
      },
      pushNotifications: {
        title: 'Sprawdź co nowego',
        textStarter: 'Masz',
        textJoiner: 'i',
        newMessages: 'nowe wiadomości ({{count}})',
        newTasks: 'nowe zadania ({{count}})',
        generic: 'Otwórzy i sprawdź co nowego'
      },
      common: {
        tryAgain: 'Spróbuj ponownie',
        minutes: '{{minutes}} min'
      },
      backgroundActivity: {
        upload: {
          default: 'Przesyłanie pliku',
          video: 'Przesyłanie video',
          audio: 'Przesyłanie audio',
          image: 'Przesyłanie obrazu'
        },
        transcript: 'Przygotowuję transkrypt…',
        default: 'Pracujemy w tle…',
        toast: {
          uploadSuccess: 'Plik przesłany'
        }
      }
    }
  }
};

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    fallbackLng: 'en',
    preload: ['en', 'pl'],
    resources: translations,
    react: {
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['strong', 'em', 'br', 'p']
    }
  });

export { i18next, translations };
